// Add product (PRODUCT-001, D-038 workflow): a three-step popup over the Products list, like the
// reference admin's wizard: 1 General information, 2 Variants and inventory, 3 Custom fields. "Next"
// checks the current step before moving on; finished steps can be revisited from the step bar. The
// right column keeps the status switch, a live quality checklist (the same rules as the server's
// `qualityScore`) and a summary. Extra product details from the second reference (features,
// specifications, SEO preview, buyer questions, hidden payment methods, similar products) live in
// step 3. Opening stock is set after the product exists, through INVENTORY-001 adjustments.
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatMoney } from "@ady/format";
import { t } from "@ady/i18n";
import { Banner, Button, cx, Icon, MoneyInput, Popup, useToast, type IconName } from "@ady/ui";
import { ApiError } from "../api/client";
import { useIdempotencyKeys } from "../api/idempotency";
import { inventoryApi, opsKeys, useLocations } from "../inventory/api";
import { useProfiles } from "../shipping/api";
import { uploadPhoto, UploadRejected } from "../media/upload";
import { useShell } from "../shell/context";
import { can } from "../shell/nav";
import {
  catalogApi,
  catalogKeys,
  useBrands,
  useCategories,
  useInstallations,
  type Axis,
  type Channel,
  type CustomField,
  type FieldType,
  type Product,
  type ProductCreate,
  type ProductStatus,
} from "./api";
import {
  combinations,
  htmlToText,
  keyOf,
  LibraryPicker,
  textToHtml,
  type MediaRef,
} from "./ProductEditor";
import {
  adminPhotoSrc,
  cleanRichHtml,
  LongDescriptionDialog,
  richContent,
} from "./LongDescriptionEditor";
import { ProductVideos, type VideoDraft } from "./ProductVideos";
import { AddColourButton, guessColour, SwatchPicker } from "./SwatchPicker";
import { nameForHex, typedHex, uniqueName } from "./colourNames";
import { catalogError, splitList, useStoreIds } from "./ui";
import { useGo } from "../shell/AppLink";
import { MethodMark } from "../orders/ui";
import { searchCrumb } from "./storePaths";

// ---------------------------------------------------------------- pure helpers

type ChannelChoice = "all" | "web" | "pos";
const CHANNELS: Record<ChannelChoice, Channel[]> = {
  all: ["web", "pos"],
  web: ["web"],
  pos: ["pos"],
};
const STATUSES = ["active", "draft", "unlisted"] as const;
type AddStatus = (typeof STATUSES)[number] | "archived";
const STEPS = ["general", "variants", "custom"] as const;
const PAY_METHODS = ["cod", "manual_qr", "fonepay_qr", "fonepay_web", "esewa", "khalti"] as const;
const FIELD_TYPES: FieldType[] = ["text", "textarea", "number", "select", "multi", "date", "image"];
const MAX_OPTIONS = 3;

interface Line {
  price: number | null;
  compareAt: number | null;
  cost: number | null;
  qty: string;
  weight: string;
  sku: string;
  barcode: string;
  hs: string;
  image: string | null;
  /** Extra barcodes, comma separated. */
  alt: string;
  off: boolean;
}
const emptyLine = (): Line => ({
  price: null,
  compareAt: null,
  cost: null,
  qty: "",
  weight: "",
  sku: "",
  barcode: "",
  hs: "",
  image: null,
  alt: "",
  off: false,
});

interface OptionDraft {
  name: string;
  values: string[];
  /** Colour preview per value (null = off for this option). */
  swatches: Record<string, string[]> | null;
}
interface QuestionDraft {
  type: FieldType;
  label: string;
  choices: string;
  required: boolean;
  surcharge: number | null;
  placeholder: string;
}

/** "0.5" kg → 500 g; null when empty, NaN when not a number. */
export function gramsOf(kg: string): number | null {
  const s = kg.trim();
  if (!s) return null;
  return /^\d+(\.\d{1,3})?$/.test(s) ? Math.round(Number(s) * 1000) : Number.NaN;
}

/** A readable SKU from the name and the option values: "Red cotton kurta" + M → "RCK-M". */
export function skuOf(name: string, values: string[]): string {
  const words = name.toUpperCase().match(/[A-Z0-9]+/g) ?? [];
  const head =
    words.length > 1
      ? words
          .slice(0, 4)
          .map((w) => w[0])
          .join("")
      : (words[0] ?? "ITEM").slice(0, 4);
  const tail = values.map((v) => (v.toUpperCase().match(/[A-Z0-9]+/g) ?? []).join("").slice(0, 4));
  return [head, ...tail].filter(Boolean).join("-").slice(0, 40);
}

const slugOf = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80) || "product";

// ---------------------------------------------------------------- dialog

/**
 * Add product, or (owner, 2026-10-22: "when I click the edit product button, it shows popup like add
 * product popup with that product existing data") edit one: with `product`, every field starts from
 * the product and saving updates it the way the product editor does (the product, then its options
 * and variants, custom fields and any stock count that changed).
 */
export function AddProductDialog({
  open,
  onClose,
  onCreated,
  product = null,
  startStep = 1,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (p: Product) => void;
  product?: Product | null;
  /** The step it opens on (edit mode: the row menu opens price and inventory, or custom fields). */
  startStep?: 1 | 2 | 3;
}) {
  return (
    // A click outside never closes it: only the X (or Esc), so a half-filled product is not lost
    // (owner, 2026-11-04).
    <Popup
      open={open}
      onClose={onClose}
      label={product ? t("catalog.add.editTitle") : t("catalog.add.title")}
      closeOnBackdrop={false}
      data-testid="catalog-add-dialog"
    >
      {open ? (
        <AddProductForm
          key={`${product?.id ?? "new"}-${startStep}`}
          onClose={onClose}
          onCreated={onCreated}
          product={product}
          startStep={startStep}
        />
      ) : null}
    </Popup>
  );
}

/** Everything the form holds, read from a saved product (edit mode). */
function initFromProduct(p: Product) {
  const channel: ChannelChoice =
    p.channels.includes("web") && p.channels.includes("pos")
      ? "all"
      : p.channels.includes("pos")
        ? "pos"
        : "web";
  const lines: Record<string, Line> = {};
  for (const v of p.variants)
    lines[keyOf(p.options, v.options)] = {
      price: v.price_paisa,
      compareAt: v.compare_at_paisa,
      cost: v.cost_paisa ?? null,
      qty: "",
      weight: v.weight_g != null ? String(v.weight_g / 1000) : "",
      sku: v.sku ?? "",
      barcode: v.barcode ?? "",
      hs: v.hs_code ?? "",
      image: v.image_asset_id,
      alt: (v.alt_barcodes ?? []).join(", "),
      off: !v.active,
    };
  // Combinations the product does not sell start switched off (only its own variants are live).
  if (p.options.length)
    for (const o of combinations(p.options)) {
      const k = keyOf(p.options, o);
      if (!lines[k]) lines[k] = { ...emptyLine(), off: true };
    }
  const fields = p.custom_fields ?? [];
  return {
    name: p.title,
    channel,
    brandId: p.brand_id ?? "",
    categoryIds: p.category_ids,
    description: htmlToText(p.description_html),
    media: p.media,
    variantsOn: p.options.length > 0,
    options: p.options.map((a, i): OptionDraft => ({
      name: a.name,
      values: a.values,
      swatches: a.swatches ?? (i === 0 ? {} : null),
    })),
    lines,
    track: p.track_inventory,
    backorder: p.allow_backorder,
    tax: p.tax_class,
    tags: p.tags,
    related: p.related_ids.map((id) => ({ id, title: id })),
    askBuyer: fields.length > 0,
    questions: fields.map((f): QuestionDraft => ({
      type: f.type,
      label: f.label,
      choices: f.options.map((o) => o.value).join(", "),
      required: f.required,
      surcharge: f.surcharge_paisa || null,
      placeholder: typeof f.config.placeholder === "string" ? f.config.placeholder : "",
    })),
    features: p.features,
    specs: p.specs.map((x) => ({ group: x.group ?? "", name: x.name, value: x.value })),
    seoTitle: p.seo.title ?? "",
    seoDescription: p.seo.description ?? "",
    hiddenPay: p.payment_exclusions,
    seoImage: p.seo.image_asset_id ?? null,
    deliveryId: p.delivery_profile_id ?? "",
    longDescription: p.long_description_html ?? "",
    videoUrl: p.video_url ?? "",
    videos: (p.videos ?? []).map((v, i): VideoDraft => ({
      asset_id: v.asset_id,
      poster_asset_id: v.poster_asset_id,
      poster_path: v.poster_path,
      duration_ms: v.duration_ms,
      file_path: v.file_path,
      name: t("catalog.add.videoName", { n: i + 1 }),
    })),
    freeDelivery: p.free_delivery ?? false,
    deliveryTime: p.delivery_time ?? "",
    courier: p.preferred_courier ?? "",
    installId: p.installation_preset_id ?? "",
    releaseDate: p.release_date ?? "",
    featuresHeading: p.features_heading ?? "",
    status: p.status as AddStatus,
  };
}

function AddProductForm({
  onClose,
  onCreated,
  product,
  startStep,
}: {
  onClose: () => void;
  onCreated: (p: Product) => void;
  product: Product | null;
  startStep: 1 | 2 | 3;
}) {
  // Edit mode: the product's own values, read once when the form opens.
  const [init] = useState(() => (product ? initFromProduct(product) : null));
  const { membership } = useShell();
  const { orgId, storeId } = useStoreIds();
  const qc = useQueryClient();
  const toast = useToast();
  const keyFor = useIdempotencyKeys();
  const canCost = can(membership, ["products.cost.read"]);
  const canStock = can(membership, ["inventory.adjust"]);
  const top = useRef<HTMLSpanElement>(null);
  const toTop = () => top.current?.scrollIntoView({ block: "start" });

  const [step, setStep] = useState<number>(startStep);
  const [reached, setReached] = useState(product ? 3 : 1);
  // The step whose "Next" failed: its problem is re-checked on every render, so the message goes
  // away as soon as it is fixed. Server answers (e.g. a SKU already used) are kept as they come.
  const [failedStep, setFailedStep] = useState<number | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Step 1
  const [name, setName] = useState(init?.name ?? "");
  const [channel, setChannel] = useState<ChannelChoice>(init?.channel ?? "all");
  const [brandId, setBrandId] = useState(init?.brandId ?? "");
  const [categoryIds, setCategoryIds] = useState<string[]>(init?.categoryIds ?? []);
  const [description, setDescription] = useState(init?.description ?? "");
  const [media, setMedia] = useState<MediaRef[]>(init?.media ?? []);
  // Step 2
  const [variantsOn, setVariantsOn] = useState(init?.variantsOn ?? false);
  const [options, setOptions] = useState<OptionDraft[]>(
    init?.options.length
      ? init.options
      : [
          { name: t("catalog.add.optionDefaults.first"), values: [], swatches: {} },
          { name: t("catalog.add.optionDefaults.second"), values: [], swatches: null },
        ],
  );
  const [lines, setLines] = useState<Record<string, Line>>(init?.lines ?? { "": emptyLine() });
  const [showCodes, setShowCodes] = useState(false);
  const [track, setTrack] = useState(init?.track ?? true);
  const [backorder, setBackorder] = useState(init?.backorder ?? false);
  // A new product starts VAT exempt (owner, 2026-11-04); an edited one keeps its saved class.
  const [tax, setTax] = useState<"standard" | "exempt" | "zero">(init?.tax ?? "exempt");
  // Step 3
  const [tags, setTags] = useState<string[]>(init?.tags ?? []);
  const [related, setRelated] = useState<{ id: string; title: string }[]>(init?.related ?? []);
  const [askBuyer, setAskBuyer] = useState(init?.askBuyer ?? false);
  const [questions, setQuestions] = useState<QuestionDraft[]>(init?.questions ?? []);
  const [features, setFeatures] = useState<string[]>(init?.features ?? []);
  const [specs, setSpecs] = useState<Spec[]>(init?.specs ?? []);
  const [seoTitle, setSeoTitle] = useState(init?.seoTitle ?? "");
  const [seoDescription, setSeoDescription] = useState(init?.seoDescription ?? "");
  const [hiddenPay, setHiddenPay] = useState<string[]>(init?.hiddenPay ?? []);
  const [seoImage, setSeoImage] = useState<string | null>(init?.seoImage ?? null);
  const [deliveryId, setDeliveryId] = useState(init?.deliveryId ?? "");
  const [photoFor, setPhotoFor] = useState<{ o: Record<string, string> } | null>(null);
  const profiles = useProfiles(orgId, storeId, true);
  const installs = useInstallations(orgId, storeId);
  const go = useGo();
  const { orgSlug, storeSlug } = useShell();
  // 1022 details
  const [longDescription, setLongDescription] = useState(init?.longDescription ?? "");
  const [showLong, setShowLong] = useState(false);
  const [videoUrl, setVideoUrl] = useState(init?.videoUrl ?? "");
  const [videos, setVideos] = useState<VideoDraft[]>(init?.videos ?? []);
  const [videoBusy, setVideoBusy] = useState(0);
  // Photos still uploading; a product needs at least one photo (owner, 2026-11-04).
  const [photoBusy, setPhotoBusy] = useState(0);
  const [photoLibrary, setPhotoLibrary] = useState(false);
  const [freeDelivery, setFreeDelivery] = useState(init?.freeDelivery ?? false);
  const [deliveryTime, setDeliveryTime] = useState(init?.deliveryTime ?? "");
  const [courier, setCourier] = useState(init?.courier ?? "");
  const [installId, setInstallId] = useState(init?.installId ?? "");
  const [releaseDate, setReleaseDate] = useState(init?.releaseDate ?? "");
  const [featuresHeading, setFeaturesHeading] = useState(init?.featuresHeading ?? "");
  const [aiBusy, setAiBusy] = useState(false);
  // Right column
  const [status, setStatus] = useState<AddStatus>(init?.status ?? "active");

  const locations = useLocations(orgId, !!product && canStock);
  const defaultLoc =
    locations.data?.data.find((l) => l.is_default)?.id ?? locations.data?.data[0]?.id;
  const stockNow = useQuery({
    queryKey: ["org", orgId, "store", storeId, "inventory", { product: product?.id, edit: true }],
    queryFn: async () => {
      const page = await inventoryApi.levels(orgId, storeId, { q: product!.title });
      return page.data.filter((r) => r.product_id === product!.id);
    },
    enabled: !!product && canStock && !!product.track_inventory,
  });
  /** The on-hand count each option started with, so only typed changes are saved. */
  const stockStart = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!product || !stockNow.data || !locations.data) return;
    const start: Record<string, string> = {};
    for (const v of product.variants) {
      const row = stockNow.data.find((r) => r.variant_id === v.id);
      if (!row) continue;
      const at = row.levels.find((l) => l.location_id === defaultLoc)?.on_hand ?? row.on_hand;
      start[keyOf(product.options, v.options)] = String(at);
    }
    stockStart.current = start;
    setLines((ls) => {
      const next = { ...ls };
      for (const [k, q] of Object.entries(start))
        if (next[k] && !next[k].qty) next[k] = { ...next[k], qty: q };
      return next;
    });
  }, [stockNow.data, locations.data]);
  const relatedTitles = useQuery({
    queryKey: catalogKeys.products(orgId, storeId, { picker: true }),
    queryFn: () => catalogApi.products(orgId, storeId, { limit: 100 }),
    enabled: !!product && product.related_ids.length > 0,
  });
  useEffect(() => {
    const found = relatedTitles.data?.data;
    if (!found) return;
    setRelated((rs) =>
      rs.map((r) => ({ ...r, title: found.find((x) => x.id === r.id)?.title ?? r.title })),
    );
  }, [relatedTitles.data]);

  const axes: Axis[] = useMemo(
    () =>
      variantsOn
        ? options
            .map((o) => {
              const sw = Object.fromEntries(
                Object.entries(o.swatches ?? {}).filter(
                  ([v, c]) => o.values.includes(v) && c.length > 0,
                ),
              );
              return {
                name: o.name.trim(),
                values: o.values,
                ...(Object.keys(sw).length ? { swatches: sw } : {}),
              };
            })
            .filter((a) => a.name && a.values.length)
        : [],
    [variantsOn, options],
  );
  const combos = useMemo(
    () => (variantsOn ? (axes.length ? combinations(axes) : []) : [{}]),
    [variantsOn, axes],
  );
  // A new combination starts from the single-product price, so switching variants on keeps it.
  const line = (k: string): Line => lines[k] ?? { ...emptyLine(), ...pick(lines[""]) };
  const setLine = (k: string, patch: Partial<Line>) =>
    setLines((ls) => ({ ...ls, [k]: { ...(ls[k] ?? line(k)), ...patch } }));
  const live = combos.filter((o) => !line(keyOf(axes, o)).off);
  const liveLines = live.map((o) => line(keyOf(axes, o)));

  // Same rules and weights as the server's qualityScore (packages/domain/product/src/model.ts).
  const checks: { id: keyof typeof QUALITY; ok: boolean }[] = [
    { id: "name", ok: name.trim().length >= 10 },
    { id: "description", ok: description.trim().length >= 50 },
    { id: "photo", ok: media.length >= 1 },
    { id: "photos", ok: media.length >= 3 },
    { id: "category", ok: categoryIds.length >= 1 },
    { id: "sku", ok: liveLines.length > 0 && liveLines.every((l) => l.sku.trim()) },
    { id: "weight", ok: liveLines.length > 0 && liveLines.every((l) => l.weight.trim()) },
    { id: "seo", ok: !!seoTitle.trim() && !!seoDescription.trim() },
  ];
  const score = Math.min(
    100,
    checks.reduce((s, c) => s + (c.ok ? QUALITY[c.id] : 0), 0),
  );

  const validate = (n: number): string | null => {
    if (n === 1) {
      if (!name.trim()) return t("catalog.add.nameRequired");
      if (photoBusy > 0) return t("catalog.add.photoUploadingCount", { count: photoBusy });
      if (!media.length) return t("catalog.add.photoRequired");
      if (videoUrl.trim() && !/^https:\/\/\S+$/i.test(videoUrl.trim()))
        return t("catalog.add.videoInvalid");
      if (videoBusy > 0) return t("catalog.add.videoUploadingCount", { count: videoBusy });
      return null;
    }
    if (n === 2) {
      if (live.length === 0) return t("catalog.add.noVariants");
      if (liveLines.some((l) => l.price === null))
        return variantsOn ? t("catalog.add.pricesRequired") : t("catalog.add.priceRequired");
      if (liveLines.some((l) => l.compareAt !== null && l.price !== null && l.compareAt <= l.price))
        return t("catalog.add.crossedLow");
      if (liveLines.some((l) => Number.isNaN(gramsOf(l.weight))))
        return t("catalog.add.weightInvalid");
      if (liveLines.some((l) => l.hs.trim() && !/^\d{4,10}$/.test(l.hs.trim())))
        return t("catalog.add.hsInvalid");
      if (
        track &&
        canStock &&
        liveLines.some((l) => l.qty.trim() && !/^\d{1,7}$/.test(l.qty.trim()))
      )
        return t("catalog.add.qtyInvalid");
      return null;
    }
    if (askBuyer && questions.some((q) => !q.label.trim()))
      return t("catalog.add.fieldLabelRequired");
    return null;
  };

  const goTo = (n: number) => {
    if (n > step) {
      for (let s = step; s < n; s++) {
        const err = validate(s);
        if (err) {
          setStep(s);
          setFailedStep(s);
          toTop();
          return;
        }
      }
    }
    setFailedStep(null);
    setStep(n);
    setReached((r) => Math.max(r, n));
    toTop();
  };

  /** One variant's body from its line (create and edit). */
  const variantOut = (o: Record<string, string>) => {
    const l = line(keyOf(axes, o));
    return {
      options: o,
      price_paisa: l.price ?? 0,
      compare_at_paisa: l.compareAt,
      ...(canCost ? { cost_paisa: l.cost } : {}),
      sku: l.sku.trim() || null,
      barcode: l.barcode.trim() || null,
      hs_code: l.hs.trim() || null,
      weight_g: gramsOf(l.weight),
      // Only a photo that is still on the product (it may have been removed in step 1).
      image_asset_id: l.image && media.some((m) => m.asset_id === l.image) ? l.image : null,
      ...(splitList(l.alt).length ? { alt_barcodes: splitList(l.alt) } : {}),
    };
  };

  const stockSaved = useRef(true);
  /** Edit mode: the product, then options and variants, custom fields and changed stock counts. */
  const saveEdit = async (): Promise<Product> => {
    const cur = product!;
    const seoImageOk = seoImage && media.some((m) => m.asset_id === seoImage) ? seoImage : null;
    let p = await catalogApi.updateProduct(
      orgId,
      storeId,
      cur.id,
      {
        title: name.trim(),
        description_html: textToHtml(description),
        status: status as ProductStatus,
        channels: CHANNELS[channel],
        brand_id: brandId || null,
        category_ids: categoryIds,
        tags,
        track_inventory: track,
        allow_backorder: backorder,
        tax_class: tax,
        media: media.map((m) => ({ asset_id: m.asset_id, alt: m.alt })),
        features: features.map((f) => f.trim()).filter(Boolean),
        specs: specs
          .map((x) => ({
            ...(x.group.trim() ? { group: x.group.trim() } : {}),
            name: x.name.trim(),
            value: x.value.trim(),
          }))
          .filter((x) => x.name && x.value),
        related_ids: related.map((r) => r.id),
        payment_exclusions: hiddenPay,
        delivery_profile_id: deliveryId || null,
        long_description_html: longDescription,
        video_url: videoUrl.trim() || null,
        videos: videos.map((v) => ({
          asset_id: v.asset_id,
          poster_asset_id: v.poster_asset_id,
          duration_ms: v.duration_ms,
        })),
        features_heading: featuresHeading.trim() || null,
        release_date: releaseDate || null,
        free_delivery: freeDelivery,
        delivery_time: deliveryTime.trim() || null,
        preferred_courier: courier || null,
        installation_preset_id: installId || null,
        seo: {
          ...(seoTitle.trim() ? { title: seoTitle.trim() } : {}),
          ...(seoDescription.trim() ? { description: seoDescription.trim() } : {}),
          image_asset_id: seoImageOk,
        },
      },
      cur.version,
    );
    // Options or the set of sold combinations changed: regenerate, like the product editor.
    const wantKeys = live.map((o) => keyOf(axes, o)).sort();
    const haveKeys = p.variants
      .filter((v) => v.active)
      .map((v) => keyOf(p.options, v.options))
      .sort();
    if (
      JSON.stringify(axes) !== JSON.stringify(p.options) ||
      JSON.stringify(wantKeys) !== JSON.stringify(haveKeys)
    ) {
      const first = live[0] ? line(keyOf(axes, live[0])).price : null;
      p = await catalogApi.generateVariants(
        orgId,
        storeId,
        p.id,
        {
          axes,
          selected_combinations: live,
          ...(first !== null ? { defaults: { price_paisa: first } } : {}),
        },
        p.version,
      );
    }
    let version = p.version;
    for (const v of p.variants.filter((x) => x.active)) {
      const { options: _o, ...want } = variantOut(v.options);
      void _o;
      const changed =
        want.price_paisa !== v.price_paisa ||
        (want.compare_at_paisa ?? null) !== v.compare_at_paisa ||
        (canCost && (want.cost_paisa ?? null) !== (v.cost_paisa ?? null)) ||
        want.sku !== v.sku ||
        want.barcode !== v.barcode ||
        want.hs_code !== v.hs_code ||
        want.weight_g !== v.weight_g ||
        want.image_asset_id !== v.image_asset_id ||
        JSON.stringify(want.alt_barcodes ?? []) !== JSON.stringify(v.alt_barcodes ?? []);
      if (!changed) continue;
      version = (
        await catalogApi.updateVariant(
          orgId,
          storeId,
          v.id,
          { alt_barcodes: [], ...want },
          v.version,
        )
      ).product_version;
    }
    const fieldsWant = askBuyer ? questions.map(questionOut) : [];
    const fieldsHave = (cur.custom_fields ?? []).map(({ id: _id, ...f }) => {
      void _id;
      return f;
    });
    if (JSON.stringify(fieldsWant) !== JSON.stringify(fieldsHave))
      version = (await catalogApi.putCustomFields(orgId, storeId, p.id, fieldsWant, version))
        .version;
    // Stock counts that were changed in step 2 (at the default location).
    stockSaved.current = true;
    if (track && canStock) {
      const fresh = version === p.version ? p : await catalogApi.product(orgId, storeId, p.id);
      for (const v of fresh.variants.filter((x) => x.active)) {
        const k = keyOf(fresh.options, v.options);
        const qty = line(keyOf(axes, v.options)).qty.trim();
        if (!qty || qty === stockStart.current[k]) continue;
        const adj = {
          variant_id: v.id,
          ...(defaultLoc ? { location_id: defaultLoc } : {}),
          set_to: Number(qty),
          reason: "count" as const,
        };
        try {
          await inventoryApi.adjust(orgId, adj, keyFor(adj));
        } catch {
          stockSaved.current = false;
        }
      }
    }
    return catalogApi.product(orgId, storeId, p.id);
  };

  const create = useMutation({
    mutationFn: async () => {
      if (product) return { p: await saveEdit(), stockOk: stockSaved.current };
      const body: ProductCreate = {
        title: name.trim(),
        description_html: textToHtml(description),
        status: status as ProductStatus,
        channels: CHANNELS[channel],
        ...(brandId ? { brand_id: brandId } : {}),
        category_ids: categoryIds,
        tags,
        track_inventory: track,
        allow_backorder: backorder,
        tax_class: tax,
        media: media.map((m) => ({ asset_id: m.asset_id, alt: m.alt })),
        features: features.map((f) => f.trim()).filter(Boolean),
        specs: specs
          .map((s) => ({
            ...(s.group.trim() ? { group: s.group.trim() } : {}),
            name: s.name.trim(),
            value: s.value.trim(),
          }))
          .filter((s) => s.name && s.value),
        related_ids: related.map((r) => r.id),
        payment_exclusions: hiddenPay,
        ...(deliveryId ? { delivery_profile_id: deliveryId } : {}),
        ...(longDescription ? { long_description_html: longDescription } : {}),
        ...(videoUrl.trim() ? { video_url: videoUrl.trim() } : {}),
        ...(videos.length
          ? {
              videos: videos.map((v) => ({
                asset_id: v.asset_id,
                poster_asset_id: v.poster_asset_id,
                duration_ms: v.duration_ms,
              })),
            }
          : {}),
        ...(featuresHeading.trim() ? { features_heading: featuresHeading.trim() } : {}),
        ...(releaseDate ? { release_date: releaseDate } : {}),
        free_delivery: freeDelivery,
        ...(deliveryTime.trim() ? { delivery_time: deliveryTime.trim() } : {}),
        ...(courier ? { preferred_courier: courier } : {}),
        ...(installId ? { installation_preset_id: installId } : {}),
        ...(seoTitle.trim() || seoDescription.trim() || seoImage
          ? {
              seo: {
                ...(seoTitle.trim() ? { title: seoTitle.trim() } : {}),
                ...(seoDescription.trim() ? { description: seoDescription.trim() } : {}),
                ...(seoImage && media.some((m) => m.asset_id === seoImage)
                  ? { image_asset_id: seoImage }
                  : {}),
              },
            }
          : {}),
        options: axes,
        variants: live.map(variantOut),
        ...(askBuyer ? { custom_fields: questions.map(questionOut) } : {}),
      };
      const p = await catalogApi.createProduct(orgId, storeId, body, keyFor(body));
      // Opening stock: one count per variant that has a quantity typed in.
      let stockOk = true;
      if (track && canStock) {
        for (const v of p.variants) {
          const qty = line(keyOf(p.options, v.options)).qty.trim();
          if (!qty) continue;
          const adj = { variant_id: v.id, set_to: Number(qty), reason: "count" as const };
          try {
            await inventoryApi.adjust(orgId, adj, keyFor(adj));
          } catch {
            stockOk = false;
          }
        }
      }
      return { p, stockOk };
    },
    onSuccess: async ({ p, stockOk }) => {
      toast.show({
        tone: "success",
        title: product
          ? t("catalog.add.saved", { title: p.title })
          : t("catalog.add.created", { title: p.title }),
      });
      if (product)
        await qc.invalidateQueries({ queryKey: catalogKeys.product(orgId, storeId, p.id) });
      if (!stockOk) toast.show({ tone: "warning", title: t("catalog.add.stockFailed") });
      await Promise.all([
        qc.invalidateQueries({ queryKey: catalogKeys.productsAll(orgId, storeId) }),
        qc.invalidateQueries({ queryKey: opsKeys.inventory(orgId, storeId) }),
      ]);
      onCreated(p);
    },
    onError: (e) => {
      setServerError(catalogError(e));
      toTop();
    },
  });

  const writeWithAi = async () => {
    if (!name.trim()) {
      toast.show({ tone: "error", title: t("catalog.add.aiNeedsName") });
      return;
    }
    setAiBusy(true);
    try {
      const out = await catalogApi.aiDescription(orgId, storeId, {
        title: name.trim(),
        description: description.trim(),
        features: features.map((f) => f.trim()).filter(Boolean),
        tags,
      });
      setDescription(out.draft.text);
      toast.show({ tone: "success", title: t("catalog.add.aiDone") });
    } catch (e) {
      toast.show({
        tone: "error",
        title:
          e instanceof ApiError && e.code === "dependency.activation_required"
            ? t("catalog.add.aiInactive")
            : catalogError(e),
      });
    } finally {
      setAiBusy(false);
    }
  };

  const submit = () => {
    for (const n of [1, 2, 3]) {
      const err = validate(n);
      if (err) {
        setStep(n);
        setFailedStep(n);
        toTop();
        return;
      }
    }
    setFailedStep(null);
    setServerError(null);
    create.mutate();
  };

  const stepError = serverError ?? (failedStep === step ? validate(step) : null);
  const nameMissing = failedStep === 1 && step === 1 && !name.trim();
  const photoProblem =
    failedStep === 1 && step === 1 && name.trim() && (photoBusy > 0 || !media.length)
      ? photoBusy > 0
        ? t("catalog.add.photoUploadingCount", { count: photoBusy })
        : t("catalog.add.photoRequired")
      : null;

  const prices = liveLines.map((l) => l.price).filter((p): p is number => p !== null);
  const stockTotal = liveLines.reduce((a, l) => a + (Number(l.qty.trim()) || 0), 0);
  const todo = checks.filter((c) => !c.ok);
  const photoTitle = photoFor === null ? "" : axes.map((a) => photoFor.o[a.name]).join(" / ");

  return (
    <div className="flex flex-col">
      <span ref={top} aria-hidden="true" />
      {/* Header and steps stay pinned while the body scrolls. */}
      <header className="sticky -top-3 z-20 -mx-3 -mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-subtle bg-sunken px-3 py-2 sm:-top-4 sm:-mx-4 sm:-mt-4 sm:px-4">
        <span className="ady-bg-brand flex size-7 items-center justify-center rounded-xs text-on-primary">
          <Icon name="box" />
        </span>
        <h2 className="text-base font-semibold">
          {product ? t("catalog.add.editTitle") : t("catalog.add.title")}
        </h2>
        <StepBar step={step} reached={reached} onPick={goTo} />
        <button
          type="button"
          onClick={onClose}
          className="ms-auto inline-flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-hover"
        >
          <Icon name="x" />
          <span className="ady-sr-only">{t("catalog.add.close")}</span>
        </button>
      </header>

      <div className="grid gap-3 pt-3 lg:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="flex min-w-0 flex-col gap-3">
          {stepError && !nameMissing ? <Banner kind="error" title={stepError} /> : null}

          {step === 1 ? (
            <>
              {/* Compact cards like step 2 (DESIGN_SYSTEM §4.3a): small sentence-case labels, related
                  fields side by side instead of full-width rows. */}
              <Section icon="box" title={t("catalog.add.secProduct")}>
                <div className="rounded-sm border border-card bg-surface p-2.5 flex flex-col gap-2.5">
                  <div className="grid gap-x-3 gap-y-2.5 md:grid-cols-[minmax(0,1fr)_17rem]">
                    <Cell label={t("catalog.add.nameLabel")} required>
                      <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("catalog.add.namePh")}
                        maxLength={200}
                        autoFocus
                        data-testid="catalog-add-name"
                        aria-invalid={nameMissing ? true : undefined}
                        className={cx(frame, "px-2 aria-invalid:border-danger-fg")}
                      />
                      {nameMissing ? (
                        <span
                          className="flex items-center gap-1 text-xs text-danger-fg"
                          role="alert"
                        >
                          <Icon name="triangle" />
                          {t("catalog.add.nameRequired")}
                        </span>
                      ) : null}
                    </Cell>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-2xs font-semibold text-fg-tertiary">
                        {t("catalog.editor.brandLabel")}
                      </span>
                      <BrandPicker value={brandId} onChange={setBrandId} />
                    </div>
                  </div>
                  <div className="grid gap-x-3 gap-y-2.5 md:grid-cols-[auto_minmax(0,1fr)]">
                    <PillGroup label={t("catalog.add.secSell")}>
                      {(Object.keys(CHANNELS) as ChannelChoice[]).map((c) => (
                        <Chip key={c} on={channel === c} onClick={() => setChannel(c)}>
                          <Icon name={c === "all" ? "globe" : c === "web" ? "store" : "cash"} />
                          {t(`catalog.add.channels.${c}`)}
                        </Chip>
                      ))}
                      <p className="w-0 min-w-full text-xs text-fg-tertiary">
                        {t(`catalog.add.channelsHelp.${channel}`)}
                      </p>
                    </PillGroup>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-2xs font-semibold text-fg-tertiary">
                        {t("catalog.editor.categoriesLabel")}
                      </span>
                      <CategoryPicker value={categoryIds} onChange={setCategoryIds} />
                    </div>
                  </div>
                </div>
              </Section>

              <Section
                icon="file"
                title={t("catalog.add.secDescription")}
                aside={
                  <span className="flex items-center gap-3 normal-case tracking-normal">
                    <span className="ady-ledger text-2xs font-medium">
                      {t("catalog.add.chars", { count: description.trim().length })}
                    </span>
                    <button
                      type="button"
                      onClick={() => void writeWithAi()}
                      disabled={aiBusy}
                      className="flex items-center gap-1 text-xs font-semibold text-link disabled:opacity-50"
                      data-testid="catalog-add-ai"
                    >
                      <Icon name="sparkle" />
                      {aiBusy ? t("catalog.add.writingAi") : t("catalog.add.writeAi")}
                    </button>
                  </span>
                }
              >
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("catalog.add.descriptionPh")}
                  aria-label={t("catalog.add.secDescription")}
                  rows={3}
                  maxLength={20000}
                  className="w-full rounded-xs border border-control bg-surface px-2.5 py-2 text-sm outline-none placeholder:text-fg-tertiary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                />
                <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
                  <details className="group text-xs text-fg-tertiary">
                    <summary className="flex cursor-pointer list-none items-center gap-1 font-semibold text-link">
                      <Icon name="info" />
                      {t("catalog.add.tipsToggle")}
                      <span className="transition-transform group-open:rotate-180">
                        <Icon name="chevronDown" />
                      </span>
                    </summary>
                    <ul className="list-disc ps-5 pt-1">
                      {(["problem", "plain", "benefits", "picture"] as const).map((k) => (
                        <li key={k}>{t(`catalog.add.tips.${k}`)}</li>
                      ))}
                    </ul>
                  </details>
                  {longDescription ? null : (
                    <button
                      type="button"
                      onClick={() => setShowLong(true)}
                      className="flex items-center gap-1 self-start text-xs font-semibold text-link"
                      data-testid="catalog-long-add"
                    >
                      <Icon name="plus" />
                      {t("catalog.add.addLongDescription")}
                    </button>
                  )}
                </div>
                {longDescription ? (
                  <div className="flex flex-col gap-1 rounded-xs border border-control bg-surface">
                    <div className="flex items-center gap-1.5 border-b border-card px-2.5 py-1 text-xs">
                      <span className="flex-1 font-semibold text-fg-secondary">
                        {t("catalog.rich.title")}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowLong(true)}
                        className="flex items-center gap-1 font-semibold text-link"
                        data-testid="catalog-long-edit"
                      >
                        <Icon name="pencil" />
                        {t("catalog.rich.edit")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setLongDescription("")}
                        className="flex items-center gap-1 font-semibold text-danger-fg"
                      >
                        <Icon name="trash" />
                        {t("catalog.rich.remove")}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowLong(true)}
                      aria-label={t("catalog.rich.previewLabel")}
                      className="relative max-h-40 overflow-hidden px-2.5 pb-2 text-start text-sm"
                    >
                      <div
                        className={richContent}
                        // Cleaned with the same allow-list as the server (no scripts, no handlers).
                        dangerouslySetInnerHTML={{
                          __html: cleanRichHtml(longDescription, adminPhotoSrc(orgId)),
                        }}
                      />
                      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-surface" />
                    </button>
                  </div>
                ) : null}
                <LongDescriptionDialog
                  open={showLong}
                  value={longDescription}
                  onClose={() => setShowLong(false)}
                  onSave={setLongDescription}
                />
              </Section>

              <Section
                icon="image"
                title={t("catalog.add.secMedia")}
                aside={
                  <button
                    type="button"
                    onClick={() => setPhotoLibrary(true)}
                    className="flex items-center gap-1 text-xs font-semibold text-link normal-case tracking-normal"
                  >
                    <Icon name="folder" />
                    {t("catalog.add.fromLibrary")}
                  </button>
                }
              >
                <div className="rounded-sm border border-card bg-surface p-2.5 flex flex-col gap-2.5">
                  {/* Videos follow the photos on the same line, after a divider: as photos are added
                      the videos move right, and wrap below once the line is full. */}
                  <div className="flex flex-wrap items-stretch gap-x-4 gap-y-3">
                    <div className="min-w-0">
                      <PhotosSection
                        media={media}
                        onChange={setMedia}
                        picking={photoLibrary}
                        setPicking={setPhotoLibrary}
                        onBusy={setPhotoBusy}
                        problem={photoProblem}
                      />
                    </div>
                    <div className="min-w-0 border-s border-card ps-4">
                      <Section
                        icon="video"
                        title={t("catalog.add.secVideos")}
                        tip={t("catalog.add.videosTip")}
                        compact
                      >
                        <ProductVideos videos={videos} onChange={setVideos} onBusy={setVideoBusy} />
                      </Section>
                    </div>
                  </div>
                  <Cell label={t("catalog.add.videoLinkLabel")} className="max-w-lg">
                    <span className="relative flex">
                      <span className="pointer-events-none absolute start-2 top-1/2 -translate-y-1/2 text-fg-tertiary">
                        <Icon name="link" />
                      </span>
                      <input
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder={t("catalog.add.videoUrl")}
                        inputMode="url"
                        maxLength={500}
                        className={cx(frame, "ps-7 pe-2")}
                      />
                    </span>
                  </Cell>
                </div>
              </Section>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <Section
                icon={variantsOn ? "layers" : "money"}
                title={t("catalog.add.secPriceStock")}
                aside={
                  <MiniSwitch
                    label={t("catalog.add.variantsSwitch")}
                    hint={t("catalog.add.variantsSwitchHelp")}
                    checked={variantsOn}
                    onChange={setVariantsOn}
                  />
                }
              >
                {variantsOn ? (
                  <>
                    <OptionsBox options={options} onChange={setOptions} />
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <CodesToggle open={showCodes} onToggle={() => setShowCodes((v) => !v)} />
                      {live.length ? (
                        <button
                          type="button"
                          onClick={() =>
                            live.forEach((o) =>
                              setLine(keyOf(axes, o), {
                                sku: skuOf(
                                  name,
                                  axes.map((a) => o[a.name] ?? ""),
                                ),
                              }),
                            )
                          }
                          className="flex items-center gap-1 text-xs font-semibold text-link"
                        >
                          <Icon name="sparkle" />
                          {t("catalog.add.generateSkus")}
                        </button>
                      ) : null}
                    </div>
                    {combos.length === 0 ? (
                      <p className="rounded-sm border border-dashed border-control px-3 py-2 text-xs text-fg-secondary">
                        {t("catalog.add.noCombos")}
                      </p>
                    ) : (
                      <VariantTable
                        axes={axes}
                        combos={combos}
                        line={(o) => line(keyOf(axes, o))}
                        setLine={(o, patch) => setLine(keyOf(axes, o), patch)}
                        canCost={canCost}
                        showQty={track && canStock}
                        showCodes={showCodes}
                        media={media}
                        onPhoto={(o) => setPhotoFor({ o })}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <SingleLine
                      l={line("")}
                      set={(patch) => setLine("", patch)}
                      canCost={canCost}
                      showQty={track && canStock}
                      showCodes={showCodes}
                      onSku={() => setLine("", { sku: skuOf(name, []) })}
                    />
                    <CodesToggle open={showCodes} onToggle={() => setShowCodes((v) => !v)} />
                  </>
                )}
              </Section>

              <Section icon="truck" title={t("catalog.add.secDelivery")}>
                {/* Two groups (DESIGN_SYSTEM §4.3a, compact): how it sells (stock, VAT) as pills, then how
                    it ships (charges, courier, installation, timing, free delivery). */}
                <div className="flex flex-col gap-2.5 rounded-sm border border-card bg-surface p-2.5">
                  <div className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
                    <PillGroup label={t("catalog.add.stockLabel")}>
                      <Chip on={track} onClick={() => setTrack(true)}>
                        <Icon name="box" />
                        {t("catalog.add.track")}
                      </Chip>
                      <Chip on={!track} onClick={() => setTrack(false)}>
                        <Icon name="infinity" />
                        {t("catalog.add.unlimited")}
                      </Chip>
                      {track ? <span aria-hidden className="mx-1 h-4 w-px bg-control" /> : null}
                      {track ? (
                        <Chip
                          on={backorder}
                          onClick={() => setBackorder(!backorder)}
                          title={t("catalog.add.backorderHelp")}
                        >
                          <Icon name={backorder ? "checkCircle" : "circle"} />
                          {t("catalog.add.backorderShort")}
                        </Chip>
                      ) : null}
                      <p className="w-full text-xs text-fg-tertiary">
                        {track ? t("catalog.add.trackHelp.on") : t("catalog.add.trackHelp.off")}
                        {track && !canStock ? ` ${t("catalog.add.qtyNoPermission")}` : ""}
                      </p>
                    </PillGroup>
                    <PillGroup label={t("catalog.add.tax")}>
                      {(["standard", "exempt", "zero"] as const).map((x) => (
                        <Chip key={x} on={tax === x} onClick={() => setTax(x)}>
                          <Icon
                            name={
                              x === "standard" ? "percent" : x === "exempt" ? "minus" : "circle"
                            }
                          />
                          {t(`catalog.editor.tax.${x}`)}
                        </Chip>
                      ))}
                      <p className="w-full text-xs text-fg-tertiary">
                        {t(`catalog.add.taxHelp.${tax}`)}
                      </p>
                    </PillGroup>
                  </div>

                  <div className="border-t border-card" role="presentation" />

                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                    <Cell label={t("catalog.add.delivery")}>
                      <select
                        value={deliveryId}
                        onChange={(e) => setDeliveryId(e.target.value)}
                        className={cx(frame, "px-2")}
                      >
                        <option value="">{t("catalog.add.deliveryDefault")}</option>
                        {(profiles.data?.data ?? [])
                          .filter((p) => !p.is_default)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                      </select>
                    </Cell>
                    <Cell label={t("catalog.add.courier")}>
                      <select
                        value={courier}
                        onChange={(e) => setCourier(e.target.value)}
                        title={t("catalog.add.courierHelp")}
                        className={cx(frame, "px-2")}
                      >
                        <option value="">{t("catalog.add.courierNone")}</option>
                        {(["pathao", "ncm"] as const).map((c) => (
                          <option key={c} value={c}>
                            {t(`catalog.add.couriers.${c}`)}
                          </option>
                        ))}
                      </select>
                    </Cell>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="flex h-3.5 items-center justify-between gap-2 text-2xs font-semibold text-fg-tertiary">
                        <label htmlFor="add-installation" className="truncate">
                          {t("catalog.add.installation")}
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            go(`/${orgSlug}/${storeSlug}/settings/delivery/installation`)
                          }
                          className="flex items-center gap-0.5 font-semibold text-link"
                        >
                          <Icon name="gear" />
                          {t("catalog.add.installationManageShort")}
                        </button>
                      </span>
                      <select
                        id="add-installation"
                        value={installId}
                        onChange={(e) => setInstallId(e.target.value)}
                        className={cx(frame, "px-2")}
                      >
                        <option value="">{t("catalog.add.installationNone")}</option>
                        {(installs.data?.data ?? [])
                          .filter((i) => i.active)
                          .map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <Cell label={t("catalog.add.deliveryTimeLabel")}>
                      <span className="relative flex">
                        <span className="pointer-events-none absolute start-2 top-1/2 -translate-y-1/2 text-fg-tertiary">
                          <Icon name="clock" />
                        </span>
                        <input
                          value={deliveryTime}
                          onChange={(e) => setDeliveryTime(e.target.value)}
                          placeholder={t("catalog.add.deliveryTimePh")}
                          title={t("catalog.add.deliveryTimeHelp")}
                          maxLength={60}
                          size={1}
                          className={cx(frame, "ps-7 pe-2")}
                        />
                      </span>
                    </Cell>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Chip
                      on={freeDelivery}
                      onClick={() => setFreeDelivery(!freeDelivery)}
                      title={t("catalog.add.freeDeliveryHelp")}
                    >
                      <Icon name="truck" />
                      {t("catalog.add.freeDeliveryShort")}
                    </Chip>
                    <p className="flex min-w-0 items-center gap-1 text-xs text-fg-tertiary">
                      <Icon name="info" />
                      {freeDelivery
                        ? t("catalog.add.freeDeliveryHelp")
                        : t("catalog.add.deliveryHelp")}
                    </p>
                  </div>
                </div>
              </Section>
            </>
          ) : null}

          {step === 3 ? (
            <>
              {/* Step 3 in compact cards (DESIGN_SYSTEM §4.3a): small labels, pills with icons for
                  on/off choices, related fields side by side. */}
              <Section icon="tag" title={t("catalog.add.secDiscovery")}>
                {/* One row: tags, similar products and the release date. */}
                <div className="grid gap-x-3 gap-y-2.5 rounded-sm border border-card bg-surface p-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_11rem]">
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <FieldLabel icon="tag">{t("catalog.editor.tagsLabel")}</FieldLabel>
                    <ChipInput
                      values={tags}
                      onChange={setTags}
                      placeholder={t("catalog.add.tagsPh")}
                      max={30}
                    />
                    <span className="text-xs text-fg-tertiary">{t("catalog.add.tagsHelp")}</span>
                  </div>
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <FieldLabel icon="link">{t("catalog.add.secSimilar")}</FieldLabel>
                    <SimilarPicker value={related} onChange={setRelated} />
                    <span className="text-xs text-fg-tertiary">{t("catalog.add.similarHelp")}</span>
                  </div>
                  <label
                    className="flex min-w-0 flex-col gap-0.5"
                    title={t("catalog.add.releaseDateHelp")}
                  >
                    <FieldLabel icon="calendar">{t("catalog.add.releaseDate")}</FieldLabel>
                    <input
                      type="date"
                      value={releaseDate}
                      onChange={(e) => setReleaseDate(e.target.value)}
                      className={cx(frame, "px-2")}
                    />
                    <span className="text-xs text-fg-tertiary">
                      {t("catalog.add.releaseDateShort")}
                    </span>
                  </label>
                </div>
              </Section>

              <Section icon="cart" title={t("catalog.add.secCheckout")}>
                <div className="rounded-sm border border-card bg-surface p-2.5 flex flex-col gap-2.5">
                  <div className="flex flex-col gap-1">
                    <FieldLabel icon="chat">{t("catalog.add.secCustom")}</FieldLabel>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Chip
                        on={askBuyer}
                        onClick={() => {
                          const on = !askBuyer;
                          setAskBuyer(on);
                          if (on && questions.length === 0) setQuestions([newQuestion()]);
                        }}
                      >
                        <Icon name={askBuyer ? "checkCircle" : "plus"} />
                        {t("catalog.add.customSwitch")}
                      </Chip>
                      <span className="min-w-0 text-xs text-fg-tertiary">
                        {t("catalog.add.customSwitchHelp")}
                      </span>
                    </div>
                    {askBuyer ? (
                      <QuestionsBox questions={questions} onChange={setQuestions} />
                    ) : null}
                  </div>
                  <div className="border-t border-card" role="presentation" />
                  <div className="flex flex-col gap-1">
                    <FieldLabel icon="card">{t("catalog.add.paymentsLabel")}</FieldLabel>
                    <div
                      role="group"
                      aria-label={t("catalog.add.paymentsLabel")}
                      className="flex flex-wrap gap-1"
                    >
                      {PAY_METHODS.map((m) => {
                        const hidden = hiddenPay.includes(m);
                        return (
                          <button
                            key={m}
                            type="button"
                            aria-pressed={!hidden}
                            onClick={() =>
                              setHiddenPay((h) => (hidden ? h.filter((x) => x !== m) : [...h, m]))
                            }
                            title={hidden ? t("catalog.add.payShow") : t("catalog.add.payHide")}
                            className={cx(
                              "flex min-h-7 items-center gap-1 rounded-full border-[1.5px] px-2.5 text-xs font-medium",
                              hidden
                                ? "border-dashed border-control bg-sunken text-fg-tertiary line-through"
                                : "border-card bg-surface text-fg hover:border-link",
                            )}
                          >
                            <MethodMark method={m} />
                            {t(`commerce.status.method.${m}`)}
                            <span className={hidden ? "text-fg-tertiary" : "text-success-fg"}>
                              <Icon name={hidden ? "x" : "check"} />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <span className="text-xs text-fg-tertiary">
                      {hiddenPay.length
                        ? t("catalog.add.paymentsHidden", { count: hiddenPay.length })
                        : t("catalog.add.paymentsAll")}
                    </span>
                  </div>
                </div>
              </Section>

              <Section icon="file" title={t("catalog.add.secPage")}>
                <div className="rounded-sm border border-card bg-surface p-2.5 flex flex-col gap-2.5">
                  <div className="flex flex-col gap-1">
                    <FieldLabel icon="checkCircle">{t("catalog.add.secFeatures")}</FieldLabel>
                    <input
                      value={featuresHeading}
                      onChange={(e) => setFeaturesHeading(e.target.value)}
                      placeholder={t("catalog.add.featuresHeadingPh")}
                      aria-label={t("catalog.add.featuresHeadingPh")}
                      maxLength={80}
                      className={cx(frame, "px-2 font-medium")}
                    />
                    <ListEditor
                      items={features}
                      onChange={setFeatures}
                      placeholder={t("catalog.add.featuresPh")}
                      addLabel={t("catalog.add.addFeature")}
                      max={20}
                    />
                  </div>
                  <div className="border-t border-card" role="presentation" />
                  <SpecsEditor specs={specs} onChange={setSpecs} />
                </div>
              </Section>

              <Section icon="globe" title={t("catalog.add.secSeo")}>
                <SeoBox
                  name={name}
                  description={description}
                  title={seoTitle}
                  onTitle={setSeoTitle}
                  meta={seoDescription}
                  onMeta={setSeoDescription}
                  media={media}
                  image={seoImage}
                  onImage={setSeoImage}
                />
              </Section>
            </>
          ) : null}
        </div>

        <aside className="flex flex-col gap-2 self-start rounded-sm border border-card bg-sunken p-2.5 lg:sticky lg:top-14">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-secondary">
              {t("catalog.add.statusLabel")}
            </span>
            <div
              role="group"
              aria-label={t("catalog.add.statusLabel")}
              className={cx(
                "grid gap-0.5 rounded-xs border border-control bg-surface p-0.5",
                product?.status === "archived" ? "grid-cols-4" : "grid-cols-3",
              )}
            >
              {(product?.status === "archived" ? [...STATUSES, "archived" as const] : STATUSES).map(
                (s) => (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={status === s}
                    onClick={() => setStatus(s)}
                    className={cx(
                      "min-h-7 rounded-xs text-xs font-medium",
                      status === s
                        ? "ady-bg-brand text-on-primary"
                        : "text-fg-secondary hover:bg-hover",
                    )}
                  >
                    {t(`catalog.add.statuses.${s}`)}
                  </button>
                ),
              )}
            </div>
            <p className="text-xs text-fg-tertiary">{t(`catalog.add.statusHelp.${status}`)}</p>
          </div>

          <div className="flex flex-col gap-1.5 rounded-sm border border-card bg-surface px-2.5 py-2">
            <span className="text-xs font-semibold">{t("catalog.add.quality", { score })}</span>
            <div className="h-1.5 overflow-hidden rounded-full bg-sunken" aria-hidden="true">
              <div
                className={cx(
                  "h-full rounded-full",
                  score >= 70 ? "bg-success-fg" : score >= 40 ? "bg-warning-fg" : "ady-bg-brand",
                )}
                style={{ width: `${score}%` }}
              />
            </div>
            {todo.length ? (
              <ul className="flex flex-col gap-0.5 text-xs text-fg-secondary">
                {todo.map((c) => (
                  <li key={c.id} className="flex items-start gap-1.5">
                    <span className="text-fg-tertiary">
                      <Icon name="circle" />
                    </span>
                    {t(`catalog.add.qualityChecks.${c.id}`)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="flex items-center gap-1 text-xs text-success-fg">
                <Icon name="checkCircle" />
                {t("catalog.add.qualityDone")}
              </p>
            )}
            <p className="text-2xs text-fg-tertiary">{t("catalog.add.qualityHelp")}</p>
          </div>

          <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 rounded-sm border border-card bg-surface px-2.5 py-1.5 text-xs">
            <Stat label={t("catalog.add.summaryPrice")} wide>
              {prices.length
                ? Math.min(...prices) === Math.max(...prices)
                  ? formatMoney(prices[0]!)
                  : `${formatMoney(Math.min(...prices))} – ${formatMoney(Math.max(...prices))}`
                : "—"}
            </Stat>
            <Stat label={t("catalog.add.summaryVariants")}>{live.length}</Stat>
            <Stat label={t("catalog.add.summaryPhotos")}>{media.length}</Stat>
            <Stat label={t("catalog.add.summaryStock")} wide>
              {track ? stockTotal : t("catalog.add.untracked")}
            </Stat>
          </dl>

          <div className="grid grid-cols-[auto_1fr] gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => (step === 1 ? onClose() : goTo(step - 1))}
            >
              {step === 1 ? t("catalog.add.cancelAdd") : t("catalog.add.back")}
            </Button>
            {step < 3 ? (
              <Button
                fullWidth
                icon="chevronRight"
                onClick={() => goTo(step + 1)}
                data-testid="catalog-add-next"
              >
                {t("catalog.add.next")}
              </Button>
            ) : (
              <Button
                fullWidth
                icon="check"
                loading={create.isPending}
                loadingLabel={product ? t("catalog.add.saving") : t("catalog.add.submitting")}
                onClick={submit}
                data-testid="catalog-add-submit"
              >
                {product ? t("catalog.add.saveChanges") : t("catalog.add.submit")}
              </Button>
            )}
          </div>
        </aside>
      </div>

      <Popup
        open={photoFor !== null}
        onClose={() => setPhotoFor(null)}
        size="sm"
        label={t("catalog.add.choosePhoto", { title: photoTitle })}
      >
        {photoFor !== null ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">
              {t("catalog.add.choosePhoto", { title: photoTitle })}
            </h3>
            <ul className="grid grid-cols-4 gap-2">
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setLine(keyOf(axes, photoFor.o), { image: null });
                    setPhotoFor(null);
                  }}
                  title={t("catalog.add.variantPhotoNone")}
                  className="flex aspect-square w-full items-center justify-center rounded-sm border border-dashed border-control bg-surface text-fg-tertiary hover:border-link hover:text-link"
                >
                  <Icon name="x" />
                  <span className="ady-sr-only">{t("catalog.add.variantPhotoNone")}</span>
                </button>
              </li>
              {media.map((m) => (
                <li key={m.asset_id}>
                  <button
                    type="button"
                    onClick={() => {
                      setLine(keyOf(axes, photoFor.o), { image: m.asset_id });
                      setPhotoFor(null);
                    }}
                    className={cx(
                      "aspect-square w-full overflow-hidden rounded-sm border bg-sunken",
                      line(keyOf(axes, photoFor.o)).image === m.asset_id
                        ? "ady-outline-brand border-[1.5px]"
                        : "border-card hover:border-link",
                    )}
                  >
                    {m.preview_path ? (
                      <img src={m.preview_path} alt={m.alt} className="size-full object-cover" />
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Popup>
    </div>
  );
}

const QUALITY = {
  name: 15,
  description: 20,
  photo: 20,
  photos: 10,
  category: 10,
  sku: 10,
  weight: 5,
  seo: 10,
} as const;

interface Spec {
  group: string;
  name: string;
  value: string;
}

const pick = (l: Line | undefined): Partial<Line> =>
  l ? { price: l.price, compareAt: l.compareAt, cost: l.cost, weight: l.weight } : {};

const newQuestion = (): QuestionDraft => ({
  type: "text",
  label: "",
  choices: "",
  required: false,
  surcharge: null,
  placeholder: "",
});

function questionOut(q: QuestionDraft): CustomField {
  const choices = q.choices
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    type: q.type,
    label: q.label.trim(),
    required: q.required,
    options: q.type === "select" || q.type === "multi" ? choices.map((value) => ({ value })) : [],
    surcharge_paisa: q.surcharge ?? 0,
    config: q.placeholder.trim() ? { placeholder: q.placeholder.trim() } : {},
  };
}

// ---------------------------------------------------------------- small pieces

const frame =
  "min-h-control w-full rounded-xs border border-control bg-surface text-sm outline-none placeholder:text-fg-tertiary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

export function Section({
  icon,
  title,
  aside,
  tip,
  compact = false,
  children,
}: {
  icon: IconName;
  title: string;
  aside?: ReactNode;
  /** Shown when the pointer is on the header icon (or it has keyboard focus). */
  tip?: string;
  /** A small sentence-case label, for a section inside a card. */
  compact?: boolean;
  children: ReactNode;
}) {
  const tipId = useId();
  return (
    <section className="flex flex-col gap-1.5">
      <h3
        className={cx(
          "flex items-center gap-1.5 text-2xs text-fg-tertiary",
          compact ? "font-semibold" : "font-bold tracking-wide uppercase",
        )}
      >
        {tip ? (
          <span
            className="group relative flex cursor-help text-link outline-none focus-visible:outline-2 focus-visible:outline-focus"
            tabIndex={0}
            aria-describedby={tipId}
          >
            <Icon name={icon} />
            <span
              id={tipId}
              role="tooltip"
              className="invisible absolute start-0 bottom-full z-20 mb-1.5 w-max max-w-72 rounded-sm bg-inverse px-2.5 py-1.5 text-xs font-normal tracking-normal text-fg-inverse normal-case opacity-0 shadow-2 transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100"
            >
              {tip}
            </span>
          </span>
        ) : (
          <span className="text-link">
            <Icon name={icon} />
          </span>
        )}
        <span className="flex-1">{title}</span>
        {aside}
      </h3>
      {children}
    </section>
  );
}

export function Chip({
  on,
  onClick,
  title,
  children,
}: {
  on: boolean;
  onClick: () => void;
  title?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      title={title}
      className={cx(
        "flex min-h-7 items-center gap-1 rounded-full border-[1.5px] px-2.5 text-xs font-medium",
        on
          ? "ady-outline-brand font-semibold text-link"
          : "border-card bg-surface text-fg-secondary hover:bg-hover",
      )}
    >
      {children}
    </button>
  );
}

/** A small sentence-case field label with an icon. */
export function FieldLabel({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <span className="flex items-center gap-1 text-2xs font-semibold text-fg-tertiary">
      <span className="text-link">
        <Icon name={icon} />
      </span>
      {children}
    </span>
  );
}

/** A small label over a row of pills (the pills are buttons, so this is a group, not a <label>). */
export function PillGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-2xs font-semibold text-fg-tertiary">{label}</span>
      <div role="group" aria-label={label} className="flex flex-wrap items-center gap-1">
        {children}
      </div>
    </div>
  );
}

function Stat({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={cx("flex items-center justify-between gap-2", wide && "col-span-2")}>
      <dt className="text-fg-tertiary">{label}</dt>
      <dd className="ady-ledger truncate font-semibold">{children}</dd>
    </div>
  );
}

/** A small labelled switch for section headers. */
function MiniSwitch({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      title={hint}
      onClick={() => onChange(!checked)}
      className="flex items-center gap-1.5 text-xs font-semibold tracking-normal text-fg-secondary normal-case"
    >
      {label}
      <span
        className={cx(
          "relative inline-flex h-4 w-7 flex-none items-center rounded-full transition-colors",
          checked ? "ady-bg-brand" : "bg-control",
        )}
      >
        <span
          className={cx(
            "absolute size-3 rounded-full bg-surface shadow-sm transition-[inset-inline-start]",
            checked ? "start-3.5" : "start-0.5",
          )}
        />
      </span>
    </button>
  );
}

function StepBar({
  step,
  reached,
  onPick,
}: {
  step: number;
  reached: number;
  onPick: (n: number) => void;
}) {
  return (
    <nav
      aria-label={t("catalog.editor.steps.label")}
      className="order-last w-full md:order-none md:w-auto md:flex-1"
    >
      <ol className="ady-bg-brand-tint flex items-center gap-1 rounded-full p-0.5">
        {STEPS.map((key, i) => {
          const n = i + 1;
          const active = n === step;
          const done = n <= reached && !active;
          return (
            <li key={key} className="flex min-w-0 flex-1">
              <button
                type="button"
                onClick={() => onPick(n)}
                disabled={n > reached + 1}
                aria-current={active ? "step" : undefined}
                className={cx(
                  "flex min-h-7 w-full min-w-0 items-center justify-center gap-1.5 rounded-full px-2 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-60",
                  active
                    ? "bg-surface font-semibold shadow-sm"
                    : "text-fg-secondary hover:bg-surface/60",
                )}
              >
                <span
                  className={cx(
                    "flex size-5 flex-none items-center justify-center rounded-full text-2xs font-bold",
                    active
                      ? "ady-bg-brand text-on-primary"
                      : done
                        ? "bg-success-bg text-success-fg"
                        : "bg-surface text-fg-secondary",
                  )}
                >
                  {done ? <Icon name="check" /> : n}
                </span>
                <span className={cx("truncate", active && "ady-text-brand")}>
                  {t(`catalog.editor.steps.${key}`)}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Type a value and press Enter (or a comma); Backspace on an empty box removes the last chip. */
export function ChipInput({
  values,
  onChange,
  placeholder,
  max,
  end,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  max: number;
  /** A control at the end of the field (e.g. the colour picker). */
  end?: ReactNode;
}) {
  const [text, setText] = useState("");
  const commit = () => {
    const parts = text
      .split(",")
      .map((s) => s.trim().slice(0, 40))
      .filter(Boolean);
    if (parts.length) onChange([...new Set([...values, ...parts])].slice(0, max));
    setText("");
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && !text && values.length) {
      onChange(values.slice(0, -1));
    }
  };
  return (
    <div className="flex min-h-control flex-wrap items-center gap-1 rounded-xs border border-control bg-surface px-1.5 py-1 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-focus">
      {values.map((v) => (
        <span
          key={v}
          className="flex items-center gap-1 rounded-xs bg-selected px-1.5 py-0.5 text-xs font-medium text-link"
          data-content="user"
        >
          {v}
          <button
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            className="text-fg-tertiary hover:text-danger-fg"
          >
            <Icon name="x" />
            <span className="ady-sr-only">{t("catalog.add.removeValue", { value: v })}</span>
          </button>
        </span>
      ))}
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        onBlur={commit}
        placeholder={values.length ? "" : placeholder}
        aria-label={placeholder}
        className="min-w-24 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-fg-tertiary"
      />
      {end}
    </div>
  );
}

function BrandPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { orgId, storeId } = useStoreIds();
  const brands = useBrands(orgId, storeId);
  const qc = useQueryClient();
  const toast = useToast();
  const keyFor = useIdempotencyKeys();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const add = useMutation({
    mutationFn: () => {
      const body = { name: newName.trim() };
      return catalogApi.createBrand(orgId, storeId, body, keyFor(body));
    },
    onSuccess: async (b) => {
      await qc.invalidateQueries({ queryKey: catalogKeys.brands(orgId, storeId) });
      onChange(b.id);
      setAdding(false);
      setNewName("");
      toast.show({ tone: "success", title: t("catalog.add.added", { name: b.name }) });
    },
    onError: (e) => toast.show({ tone: "error", title: catalogError(e) }),
  });
  return adding ? (
    <InlineAdd
      value={newName}
      onValue={setNewName}
      placeholder={t("catalog.add.newBrandPh")}
      busy={add.isPending}
      onSave={() => add.mutate()}
      onCancel={() => setAdding(false)}
    />
  ) : (
    <div className="flex gap-1.5">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={t("catalog.editor.brandLabel")}
        className={cx(frame, "px-2.5")}
      >
        <option value="">{t("catalog.add.brandNone")}</option>
        {(brands.data?.data ?? []).map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
      <AddButton onClick={() => setAdding(true)} label={t("catalog.add.addBrand")} />
    </div>
  );
}

function CategoryPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { orgId, storeId } = useStoreIds();
  const cats = useCategories(orgId, storeId);
  const qc = useQueryClient();
  const toast = useToast();
  const keyFor = useIdempotencyKeys();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const add = useMutation({
    mutationFn: () => {
      const body = { name: newName.trim() };
      return catalogApi.createCategory(orgId, storeId, body, keyFor(body));
    },
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: catalogKeys.categories(orgId, storeId) });
      onChange([...value, c.id]);
      setAdding(false);
      setNewName("");
      toast.show({ tone: "success", title: t("catalog.add.added", { name: c.name }) });
    },
    onError: (e) => toast.show({ tone: "error", title: catalogError(e) }),
  });
  const list = cats.data?.data ?? [];
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-1.5">
        <div className="flex max-h-24 min-h-control flex-1 flex-wrap items-center gap-1 overflow-y-auto rounded-xs border border-control bg-surface p-1">
          {list.length ? (
            list.map((c) => {
              const on = value.includes(c.id);
              return (
                <Chip
                  key={c.id}
                  on={on}
                  onClick={() => onChange(on ? value.filter((x) => x !== c.id) : [...value, c.id])}
                >
                  {on ? <Icon name="check" /> : null}
                  <span data-content="user">{c.name}</span>
                </Chip>
              );
            })
          ) : (
            <span className="px-1.5 text-xs text-fg-tertiary">
              {t("catalog.add.categoriesNone")}
            </span>
          )}
        </div>
        {!adding ? (
          <AddButton onClick={() => setAdding(true)} label={t("catalog.add.addCategory")} />
        ) : null}
      </div>
      {adding ? (
        <InlineAdd
          value={newName}
          onValue={setNewName}
          placeholder={t("catalog.add.newCategoryPh")}
          busy={add.isPending}
          onSave={() => add.mutate()}
          onCancel={() => setAdding(false)}
        />
      ) : null}
    </div>
  );
}

function AddButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-control flex-none items-center gap-1 rounded-xs bg-selected px-2.5 text-xs font-semibold text-link hover:bg-hover"
    >
      <Icon name="plus" />
      {label}
    </button>
  );
}

function InlineAdd({
  value,
  onValue,
  placeholder,
  busy,
  onSave,
  onCancel,
}: {
  value: string;
  onValue: (v: string) => void;
  placeholder: string;
  busy: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex gap-1.5">
      <input
        value={value}
        onChange={(e) => onValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) {
            e.preventDefault();
            onSave();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        maxLength={80}
        autoFocus
        className={cx(frame, "px-2.5")}
      />
      <Button size="md" loading={busy} disabled={!value.trim()} onClick={onSave}>
        {t("catalog.add.save")}
      </Button>
      <Button size="md" variant="ghost" onClick={onCancel}>
        {t("catalog.add.cancelAdd")}
      </Button>
    </div>
  );
}

function PhotosSection({
  media,
  onChange,
  picking,
  setPicking,
  onBusy,
  problem,
}: {
  media: MediaRef[];
  onChange: (m: MediaRef[]) => void;
  /** The photo library picker; its button sits at the right end of the media row. */
  picking: boolean;
  setPicking: (open: boolean) => void;
  /** How many photos are still uploading (Next step waits for them). */
  onBusy?: (n: number) => void;
  /** Why the step cannot go on yet (no photo, or photos still uploading), shown under the tile. */
  problem?: string | null;
}) {
  const { orgId, storeId } = useStoreIds();
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const latest = useRef(media);
  useEffect(() => {
    latest.current = media;
  }, [media]);

  const onFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = "";
    if (!files.length) return;
    setProgress({ done: 0, total: files.length });
    onBusy?.(files.length);
    for (const [i, f] of files.entries()) {
      try {
        const asset = await uploadPhoto(orgId, storeId, f, crypto.randomUUID());
        latest.current = [
          ...latest.current,
          { asset_id: asset.id, alt: "", preview_path: asset.preview_path },
        ];
        onChange(latest.current);
      } catch (err) {
        const key =
          err instanceof UploadRejected
            ? err.reason === "type"
              ? "catalog.media.typeNotAllowed"
              : "catalog.media.tooLarge"
            : "catalog.media.uploadFailed";
        toast.show({
          tone: "error",
          title: t(key, { name: f.name }),
          ...(err instanceof ApiError ? { body: catalogError(err) } : {}),
        });
      }
      setProgress({ done: i + 1, total: files.length });
      onBusy?.(files.length - i - 1);
    }
    setProgress(null);
    onBusy?.(0);
    void qc.invalidateQueries({ queryKey: catalogKeys.media(orgId) });
  };

  return (
    <Section
      icon="image"
      title={t("catalog.add.secPhotos")}
      tip={t("catalog.add.photosTip")}
      compact
    >
      <ul className="flex flex-wrap gap-2" aria-label={t("catalog.media.gridLabel")}>
        {media.map((m, i) => (
          <li
            key={m.asset_id}
            className={cx(
              "relative size-20 overflow-hidden rounded-sm border bg-sunken",
              i === 0 ? "ady-outline-brand border-[1.5px]" : "border-card",
            )}
          >
            {m.preview_path ? (
              <img src={m.preview_path} alt={m.alt} className="size-full object-cover" />
            ) : null}
            {i === 0 ? (
              <span className="ady-bg-brand absolute start-1 top-1 rounded-xs px-1 text-2xs font-semibold text-on-primary">
                {t("catalog.add.cover")}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onChange([m, ...media.filter((x) => x.asset_id !== m.asset_id)])}
                className="absolute inset-x-1 bottom-1 rounded-xs bg-surface/90 px-1 text-2xs font-semibold text-link hover:bg-surface"
              >
                {t("catalog.add.makeCover")}
              </button>
            )}
            <button
              type="button"
              onClick={() => onChange(media.filter((x) => x.asset_id !== m.asset_id))}
              title={t("catalog.add.removePhoto")}
              className="absolute end-1 top-1 flex size-5 items-center justify-center rounded-full bg-surface/90 text-fg-secondary hover:text-danger-fg"
            >
              <Icon name="x" />
              <span className="ady-sr-only">{t("catalog.add.removePhoto")}</span>
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={() => input.current?.click()}
            disabled={!!progress}
            aria-invalid={problem ? true : undefined}
            className="flex size-20 flex-col items-center justify-center gap-1 rounded-sm border border-dashed border-control bg-surface text-xs text-fg-secondary hover:border-link hover:bg-selected hover:text-link aria-invalid:border-danger-fg aria-invalid:text-danger-fg"
          >
            <Icon name={progress ? "clock" : "upload"} />
            {progress ? t("catalog.media.uploading", progress) : t("catalog.add.uploadTile")}
          </button>
        </li>
      </ul>
      {problem ? (
        <span className="flex items-center gap-1 text-xs text-danger-fg" role="alert">
          <Icon name="triangle" />
          {problem}
        </span>
      ) : null}
      <p className="text-xs text-fg-tertiary">{t("catalog.add.photosHelp")}</p>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        hidden
        onChange={(e) => void onFiles(e)}
      />
      <LibraryPicker
        open={picking}
        onClose={() => setPicking(false)}
        exclude={media.map((m) => m.asset_id)}
        onPick={(picked) => {
          onChange([...media, ...picked]);
          setPicking(false);
        }}
      />
    </Section>
  );
}

function CodesToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex items-center gap-1 self-start text-xs font-semibold text-link"
    >
      {open ? t("catalog.add.hideCodes") : t("catalog.add.showCodes")}
      <span className={cx("transition-transform", open && "rotate-180")}>
        <Icon name="chevronDown" />
      </span>
    </button>
  );
}

function SingleLine({
  l,
  set,
  canCost,
  showQty,
  showCodes,
  onSku,
}: {
  l: Line;
  set: (p: Partial<Line>) => void;
  canCost: boolean;
  showQty: boolean;
  showCodes: boolean;
  onSku: () => void;
}) {
  // One compact row (prices, then stock and shipping), sized like the variant table's cells.
  const money = "px-2";
  return (
    <div className="grid grid-cols-2 gap-x-2 gap-y-2 rounded-sm border border-card bg-surface p-2.5 sm:grid-cols-3 lg:grid-cols-6">
      <Cell label={t("catalog.add.crossedPrice")}>
        <MoneyInput
          value={l.compareAt}
          onValueChange={(v) => set({ compareAt: v })}
          size={1}
          className={money}
        />
      </Cell>
      <Cell label={t("catalog.add.sellingPrice")} required>
        <MoneyInput
          value={l.price}
          onValueChange={(v) => set({ price: v })}
          size={1}
          className={money}
          data-testid="catalog-add-price"
        />
      </Cell>
      {canCost ? (
        <Cell label={t("catalog.add.costPrice")}>
          <MoneyInput
            value={l.cost}
            onValueChange={(v) => set({ cost: v })}
            size={1}
            className={money}
          />
        </Cell>
      ) : null}
      {showQty ? (
        <Cell label={t("catalog.add.quantity")}>
          <input
            value={l.qty}
            onChange={(e) => set({ qty: e.target.value.replace(/[^\d]/g, "") })}
            inputMode="numeric"
            size={1}
            className={cx(frame, "ady-ledger px-2")}
          />
        </Cell>
      ) : null}
      <Cell label={t("catalog.add.weight")}>
        <span className="relative flex">
          <input
            value={l.weight}
            onChange={(e) => set({ weight: e.target.value.replace(/[^\d.]/g, "") })}
            inputMode="decimal"
            size={1}
            className={cx(frame, "ady-ledger ps-2 pe-7")}
          />
          <span className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 text-xs text-fg-tertiary">
            {t("catalog.add.kg")}
          </span>
        </span>
      </Cell>
      <Cell label={t("catalog.add.sku")}>
        <span className="relative flex">
          <input
            value={l.sku}
            onChange={(e) => set({ sku: e.target.value })}
            spellCheck={false}
            maxLength={40}
            size={1}
            className={cx(frame, "ps-2 pe-7")}
          />
          <button
            type="button"
            onClick={onSku}
            title={t("catalog.add.generateSku")}
            className="absolute end-0.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-xs text-link hover:bg-hover"
          >
            <Icon name="sparkle" />
            <span className="ady-sr-only">{t("catalog.add.generateSku")}</span>
          </button>
        </span>
      </Cell>
      {showCodes ? (
        <>
          <Cell label={t("catalog.add.barcode")} className="lg:col-span-2">
            <input
              value={l.barcode}
              onChange={(e) => set({ barcode: e.target.value })}
              inputMode="numeric"
              maxLength={40}
              size={1}
              className={cx(frame, "ady-ledger px-2")}
            />
          </Cell>
          <Cell label={t("catalog.add.hsCode")}>
            <input
              value={l.hs}
              onChange={(e) => set({ hs: e.target.value.replace(/[^\d]/g, "") })}
              inputMode="numeric"
              maxLength={10}
              size={1}
              className={cx(frame, "ady-ledger px-2")}
            />
          </Cell>
          <Cell label={t("catalog.add.altBarcodes")} className="col-span-2 sm:col-span-3">
            <ChipInput
              values={splitList(l.alt)}
              onChange={(v) => set({ alt: v.join(", ") })}
              placeholder={t("catalog.add.altBarcodesPh")}
              max={10}
            />
          </Cell>
        </>
      ) : null}
    </div>
  );
}

/** A compact field: small label over the control (Price and stock). */
export function Cell({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cx("flex min-w-0 flex-col gap-0.5", className)}>
      <span className="truncate text-2xs font-semibold text-fg-tertiary">
        {label}
        {required ? <span className="text-danger-fg"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

function OptionsBox({
  options,
  onChange,
}: {
  options: OptionDraft[];
  onChange: (o: OptionDraft[]) => void;
}) {
  // With the colour preview on, a value named after a colour ("Red", "Navy blue") gets it at once.
  const withGuesses = (o: OptionDraft): OptionDraft => {
    if (o.swatches === null) return o;
    const sw = { ...o.swatches };
    let changed = false;
    for (const v of o.values) {
      if (v in sw) continue;
      const g = guessColour(v);
      if (g) {
        sw[v] = [g];
        changed = true;
      }
    }
    return changed ? { ...o, swatches: sw } : o;
  };
  const upd = (i: number, patch: Partial<OptionDraft>) =>
    onChange(options.map((o, j) => (j === i ? withGuesses({ ...o, ...patch }) : o)));
  // With the colour preview on, a hex code typed as a value ("#1F6F43") becomes the closest colour
  // name (ΔE00, colourNames) and keeps the exact colour as its swatch.
  const setValues = (i: number, o: OptionDraft, values: string[]) => {
    if (o.swatches === null) return upd(i, { values });
    const sw = { ...o.swatches };
    const out: string[] = [];
    for (const v of values) {
      const hex = o.values.includes(v) ? null : typedHex(v);
      if (!hex) {
        out.push(v);
        continue;
      }
      const name = uniqueName(nameForHex(hex), [...out, ...values.filter((x) => !typedHex(x))]);
      out.push(name);
      sw[name] = [hex];
    }
    upd(i, { values: [...new Set(out)], swatches: sw });
  };
  return (
    // Compact, like the price row: small column labels, "Add option" in the header line, and the
    // colour chips lined up under the values.
    <div className="flex flex-col gap-1.5 rounded-sm border border-card bg-surface p-2.5">
      <div className="grid grid-cols-[7.5rem_minmax(0,1fr)_auto] items-end gap-x-1.5 text-2xs font-semibold text-fg-tertiary">
        <span>{t("catalog.add.optionCol")}</span>
        <span>{t("catalog.add.valuesCol")}</span>
        {options.length < MAX_OPTIONS ? (
          <button
            type="button"
            onClick={() => onChange([...options, { name: "", values: [], swatches: null }])}
            className="flex items-center gap-1 text-xs font-semibold text-link"
            data-testid="catalog-add-option"
          >
            <Icon name="plus" />
            {t("catalog.add.addOptionShort")}
          </button>
        ) : (
          <span className="font-normal">{t("catalog.add.maxOptions")}</span>
        )}
      </div>
      {options.map((o, i) => (
        <div key={i} className="grid grid-cols-[7.5rem_minmax(0,1fr)_auto] gap-x-1.5 gap-y-1">
          <input
            value={o.name}
            onChange={(e) => upd(i, { name: e.target.value })}
            placeholder={t("catalog.add.optionNamePh")}
            aria-label={t("catalog.add.optionNamePh")}
            maxLength={40}
            size={1}
            className={cx(frame, "px-2 font-medium")}
          />
          <ChipInput
            values={o.values}
            onChange={(values) => setValues(i, o, values)}
            placeholder={
              o.swatches !== null
                ? t("catalog.add.colourValuesPh")
                : t("catalog.add.optionValuesPh")
            }
            max={50}
            end={
              o.swatches !== null ? (
                <AddColourButton
                  taken={o.values}
                  onAdd={(name, hex) =>
                    upd(i, {
                      values: [...o.values, name],
                      swatches: { ...(o.swatches ?? {}), [name]: [hex] },
                    })
                  }
                />
              ) : null
            }
          />
          <button
            type="button"
            onClick={() => onChange(options.filter((_, j) => j !== i))}
            title={t("catalog.add.removeOption", { name: o.name })}
            className="flex min-h-control w-7 items-center justify-center rounded-xs text-fg-tertiary hover:bg-hover hover:text-danger-fg"
          >
            <Icon name="trash" />
            <span className="ady-sr-only">{t("catalog.add.removeOption", { name: o.name })}</span>
          </button>
          {o.values.length ? (
            <div className="col-span-2 col-start-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <MiniSwitch
                label={t("catalog.add.swatchesToggle")}
                checked={o.swatches !== null}
                onChange={(on) => upd(i, { swatches: on ? {} : null })}
              />
              {o.swatches
                ? o.values.map((v) => (
                    <SwatchPicker
                      key={v}
                      value={v}
                      colors={o.swatches?.[v] ?? []}
                      onChange={(next) =>
                        upd(i, { swatches: { ...(o.swatches ?? {}), [v]: next } })
                      }
                    />
                  ))
                : null}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function VariantTable({
  axes,
  combos,
  line,
  setLine,
  canCost,
  showQty,
  showCodes,
  media,
  onPhoto,
}: {
  axes: Axis[];
  combos: Record<string, string>[];
  line: (o: Record<string, string>) => Line;
  setLine: (o: Record<string, string>, patch: Partial<Line>) => void;
  canCost: boolean;
  showQty: boolean;
  showCodes: boolean;
  media: MediaRef[];
  onPhoto: (o: Record<string, string>) => void;
}) {
  const cell = cx(frame, "ady-ledger min-w-0 px-2");
  const off = combos.filter((o) => line(o).off);
  return (
    <div className="overflow-x-auto rounded-sm border border-card bg-surface">
      <table className={cx("w-full text-xs", showCodes ? "min-w-[60rem]" : "min-w-[36rem]")}>
        <thead className="bg-sunken text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
          <tr>
            <th className="w-px px-2 py-1.5 text-start whitespace-nowrap">
              {t("catalog.add.variantCol")}
            </th>
            <th className="px-1 py-1.5 text-start">{t("catalog.add.crossedPrice")}</th>
            <th className="px-1 py-1.5 text-start">{t("catalog.add.sellingPrice")} *</th>
            {canCost ? (
              <th className="px-1 py-1.5 text-start">{t("catalog.add.costPrice")}</th>
            ) : null}
            <th className="px-1 py-1.5 text-start">{t("catalog.add.weightKg")}</th>
            {showQty ? (
              <th className="px-1 py-1.5 text-start">{t("catalog.add.quantity")}</th>
            ) : null}
            <th className="px-1 py-1.5 text-start">{t("catalog.add.sku")}</th>
            {showCodes ? (
              <>
                <th className="px-1 py-1.5 text-start">{t("catalog.add.barcode")}</th>
                <th className="px-1 py-1.5 text-start">{t("catalog.add.hsCode")}</th>
                <th className="px-1 py-1.5 text-start">{t("catalog.add.altBarcodes")}</th>
              </>
            ) : null}
            <th className="w-7" />
          </tr>
        </thead>
        <tbody className="divide-y divide-subtle">
          {combos
            .filter((o) => !line(o).off)
            .map((o) => {
              const l = line(o);
              const title = axes.map((a) => o[a.name]).join(" / ");
              return (
                <tr key={keyOf(axes, o)} data-testid="catalog-add-variant">
                  <td className="px-2 py-1 font-semibold whitespace-nowrap" data-content="user">
                    <span className="flex items-center gap-2">
                      <VariantPhoto
                        media={media}
                        image={l.image}
                        title={title}
                        onClick={() => onPhoto(o)}
                      />
                      {title}
                    </span>
                  </td>
                  <td className="w-28 px-1 py-1">
                    <MoneyInput
                      value={l.compareAt}
                      onValueChange={(v) => setLine(o, { compareAt: v })}
                      size={1}
                      className="px-2"
                      aria-label={`${t("catalog.add.crossedPrice")} ${title}`}
                    />
                  </td>
                  <td className="w-28 px-1 py-1">
                    <MoneyInput
                      value={l.price}
                      onValueChange={(v) => setLine(o, { price: v })}
                      size={1}
                      className="px-2"
                      aria-label={`${t("catalog.add.sellingPrice")} ${title}`}
                    />
                  </td>
                  {canCost ? (
                    <td className="w-28 px-1 py-1">
                      <MoneyInput
                        value={l.cost}
                        onValueChange={(v) => setLine(o, { cost: v })}
                        size={1}
                        className="px-2"
                        aria-label={`${t("catalog.add.costPrice")} ${title}`}
                      />
                    </td>
                  ) : null}
                  <td className="w-16 px-1 py-1">
                    <input
                      value={l.weight}
                      onChange={(e) =>
                        setLine(o, { weight: e.target.value.replace(/[^\d.]/g, "") })
                      }
                      inputMode="decimal"
                      aria-label={`${t("catalog.add.weightKg")} ${title}`}
                      size={1}
                      className={cell}
                    />
                  </td>
                  {showQty ? (
                    <td className="w-16 px-1 py-1">
                      <input
                        value={l.qty}
                        onChange={(e) => setLine(o, { qty: e.target.value.replace(/[^\d]/g, "") })}
                        inputMode="numeric"
                        aria-label={`${t("catalog.add.quantity")} ${title}`}
                        size={1}
                        className={cell}
                      />
                    </td>
                  ) : null}
                  <td className="px-1 py-1">
                    <input
                      value={l.sku}
                      onChange={(e) => setLine(o, { sku: e.target.value })}
                      spellCheck={false}
                      maxLength={40}
                      aria-label={`${t("catalog.add.sku")} ${title}`}
                      size={1}
                      className={cell}
                    />
                  </td>
                  {showCodes ? (
                    <>
                      <td className="px-1 py-1">
                        <input
                          value={l.barcode}
                          onChange={(e) => setLine(o, { barcode: e.target.value })}
                          maxLength={40}
                          aria-label={`${t("catalog.add.barcode")} ${title}`}
                          size={1}
                          className={cell}
                        />
                      </td>
                      <td className="w-20 px-1 py-1">
                        <input
                          value={l.hs}
                          onChange={(e) => setLine(o, { hs: e.target.value.replace(/[^\d]/g, "") })}
                          maxLength={10}
                          aria-label={`${t("catalog.add.hsCode")} ${title}`}
                          size={1}
                          className={cell}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <input
                          value={l.alt}
                          onChange={(e) => setLine(o, { alt: e.target.value })}
                          placeholder={t("catalog.add.altBarcodesCellPh")}
                          title={t("catalog.add.altBarcodesHelp")}
                          aria-label={`${t("catalog.add.altBarcodes")} ${title}`}
                          size={1}
                          className={cell}
                        />
                      </td>
                    </>
                  ) : null}
                  <td className="px-1 py-1 text-center">
                    <button
                      type="button"
                      onClick={() => setLine(o, { off: true })}
                      title={t("catalog.add.removeVariant", { title })}
                      className="text-fg-tertiary hover:text-danger-fg"
                    >
                      <Icon name="trash" />
                      <span className="ady-sr-only">
                        {t("catalog.add.removeVariant", { title })}
                      </span>
                    </button>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      {off.length ? (
        <button
          type="button"
          onClick={() => off.forEach((o) => setLine(o, { off: false }))}
          className="w-full border-t border-subtle px-2 py-1.5 text-start text-xs font-semibold text-link hover:bg-hover"
        >
          {t("catalog.add.removedCount", { count: off.length })}
        </button>
      ) : null}
    </div>
  );
}

function SimilarPicker({
  value,
  onChange,
}: {
  value: { id: string; title: string }[];
  onChange: (v: { id: string; title: string }[]) => void;
}) {
  const { orgId, storeId } = useStoreIds();
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const results = useQuery({
    queryKey: catalogKeys.products(orgId, storeId, { q: query, limit: 8, similar: true }),
    queryFn: () => catalogApi.products(orgId, storeId, { q: query || undefined, limit: 8 }),
    enabled: focused,
  });
  const rows = (results.data?.data ?? []).filter((p) => !value.some((v) => v.id === p.id));
  return (
    <div className="flex flex-col gap-1.5">
      {value.length ? (
        <div className="flex flex-wrap gap-1">
          {value.map((v) => (
            <span
              key={v.id}
              className="flex max-w-full items-center gap-1 rounded-xs bg-selected px-1.5 py-0.5 text-xs font-medium text-link"
            >
              <span className="truncate" data-content="user">
                {v.title}
              </span>
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x.id !== v.id))}
                className="text-fg-tertiary hover:text-danger-fg"
              >
                <Icon name="x" />
                <span className="ady-sr-only">
                  {t("catalog.add.removeValue", { value: v.title })}
                </span>
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="relative">
        <span className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-fg-tertiary">
          <Icon name="search" />
        </span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={t("catalog.add.similarPh")}
          aria-label={t("catalog.add.similarPh")}
          className={cx(frame, "ps-8 pe-2.5")}
        />
        {focused && value.length < 20 ? (
          <ul className="absolute inset-x-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-sm border border-card bg-surface py-1 shadow-md">
            {rows.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange([...value, { id: p.id, title: p.title }]);
                    setQ("");
                  }}
                  className="flex w-full items-center gap-2 px-2.5 py-1 text-start text-sm hover:bg-hover"
                >
                  {p.image ? (
                    <img
                      src={p.image.preview_path}
                      alt=""
                      className="size-6 rounded-xs object-cover"
                    />
                  ) : (
                    <span className="flex size-6 items-center justify-center rounded-xs bg-sunken text-fg-tertiary">
                      <Icon name="box" />
                    </span>
                  )}
                  <span className="truncate" data-content="user">
                    {p.title}
                  </span>
                </button>
              </li>
            ))}
            {results.isSuccess && rows.length === 0 ? (
              <li className="px-2.5 py-1 text-xs text-fg-tertiary">
                {t("catalog.add.similarEmpty")}
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function QuestionsBox({
  questions,
  onChange,
}: {
  questions: QuestionDraft[];
  onChange: (q: QuestionDraft[]) => void;
}) {
  const upd = (i: number, patch: Partial<QuestionDraft>) =>
    onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  return (
    <div className="flex flex-col gap-2">
      {questions.map((q, i) => (
        <div
          key={i}
          className="grid gap-1.5 rounded-sm border border-card bg-surface p-2.5 sm:grid-cols-[minmax(0,1fr)_9rem]"
        >
          <input
            value={q.label}
            onChange={(e) => upd(i, { label: e.target.value })}
            placeholder={t("catalog.add.fieldLabelPh")}
            aria-label={t("catalog.add.fieldLabelPh")}
            maxLength={80}
            className={cx(frame, "px-2.5")}
          />
          <select
            value={q.type}
            onChange={(e) => upd(i, { type: e.target.value as FieldType })}
            aria-label={t("catalog.customFields.typeLabel")}
            className={cx(frame, "px-2.5")}
          >
            {FIELD_TYPES.map((ty) => (
              <option key={ty} value={ty}>
                {t(`catalog.customFields.types.${ty}`)}
              </option>
            ))}
          </select>
          {q.type === "select" || q.type === "multi" ? (
            <input
              value={q.choices}
              onChange={(e) => upd(i, { choices: e.target.value })}
              placeholder={t("catalog.add.fieldChoicesPh")}
              aria-label={t("catalog.add.fieldChoicesPh")}
              className={cx(frame, "px-2.5 sm:col-span-2")}
            />
          ) : (
            <input
              value={q.placeholder}
              onChange={(e) => upd(i, { placeholder: e.target.value })}
              placeholder={t("catalog.add.fieldPlaceholderPh")}
              aria-label={t("catalog.add.fieldPlaceholderPh")}
              maxLength={120}
              className={cx(frame, "px-2.5 text-fg-secondary sm:col-span-2")}
            />
          )}
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={q.required}
                onChange={(e) => upd(i, { required: e.target.checked })}
                className="size-4 accent-(--ady-color-action-primary-bg)"
              />
              {t("catalog.add.fieldRequired")}
            </label>
            <label className="flex items-center gap-1.5 text-xs">
              {t("catalog.add.fieldSurcharge")}
              <span className="w-24">
                <MoneyInput value={q.surcharge} onValueChange={(v) => upd(i, { surcharge: v })} />
              </span>
            </label>
            <button
              type="button"
              onClick={() => onChange(questions.filter((_, j) => j !== i))}
              className="ms-auto flex items-center gap-1 text-xs text-fg-tertiary hover:text-danger-fg"
            >
              <Icon name="trash" />
              {t("catalog.add.removeField")}
            </button>
          </div>
        </div>
      ))}
      {questions.length < 20 ? (
        <button
          type="button"
          onClick={() => onChange([...questions, newQuestion()])}
          className="flex items-center gap-1 self-start text-xs font-semibold text-link"
        >
          <Icon name="plus" />
          {t("catalog.add.addField")}
        </button>
      ) : null}
    </div>
  );
}

function ListEditor({
  items,
  onChange,
  placeholder,
  addLabel,
  max,
}: {
  items: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
  addLabel: string;
  max: number;
}) {
  const shown = items.length ? items : [""];
  // The cursor follows a new line (Enter or "Add feature").
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusAt, setFocusAt] = useState<number | null>(null);
  useEffect(() => {
    if (focusAt === null) return;
    refs.current[focusAt]?.focus();
    setFocusAt(null);
  }, [focusAt, shown.length]);
  return (
    <div className="flex flex-col gap-1">
      {shown.map((it, i) => (
        <div key={i} className="flex items-center gap-1">
          <span className="relative flex min-w-0 flex-1">
            <span className="pointer-events-none absolute start-2 top-1/2 -translate-y-1/2 text-success-fg">
              <Icon name="check" />
            </span>
            <input
              ref={(el) => {
                refs.current[i] = el;
              }}
              value={it}
              onChange={(e) => {
                const next = [...shown];
                next[i] = e.target.value;
                onChange(next);
              }}
              onKeyDown={(e) => {
                // Enter adds the next feature line.
                if (e.key === "Enter" && it.trim() && shown.length < max) {
                  e.preventDefault();
                  onChange([...shown.slice(0, i + 1), "", ...shown.slice(i + 1)]);
                  setFocusAt(i + 1);
                }
              }}
              placeholder={placeholder}
              aria-label={placeholder}
              maxLength={200}
              className={cx(frame, "ps-7 pe-2")}
            />
          </span>
          <button
            type="button"
            onClick={() => onChange(shown.filter((_, j) => j !== i))}
            title={t("catalog.add.remove")}
            className="flex min-h-control w-7 items-center justify-center rounded-xs text-fg-tertiary hover:bg-hover hover:text-danger-fg"
          >
            <Icon name="trash" />
            <span className="ady-sr-only">{t("catalog.add.remove")}</span>
          </button>
        </div>
      ))}
      {shown.length < max ? (
        <button
          type="button"
          onClick={() => {
            onChange([...shown, ""]);
            setFocusAt(shown.length);
          }}
          className="flex min-h-7 items-center gap-1 self-start rounded-full border border-dashed border-control px-2.5 text-xs font-semibold text-link hover:border-link"
        >
          <Icon name="plus" />
          {addLabel}
        </button>
      ) : null}
    </div>
  );
}

function SpecsEditor({ specs, onChange }: { specs: Spec[]; onChange: (s: Spec[]) => void }) {
  const shown = specs.length ? specs : [{ group: "", name: "", value: "" }];
  const upd = (i: number, patch: Partial<Spec>) =>
    onChange(shown.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const cols = "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)_1.75rem] gap-x-1";
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <FieldLabel icon="list">{t("catalog.add.secSpecs")}</FieldLabel>
        {shown.length < 60 ? (
          <button
            type="button"
            onClick={() =>
              onChange([
                ...shown,
                { group: shown[shown.length - 1]?.group ?? "", name: "", value: "" },
              ])
            }
            className="flex items-center gap-1 text-xs font-semibold text-link"
          >
            <Icon name="plus" />
            {t("catalog.add.addSpecShort")}
          </button>
        ) : null}
      </div>
      <div className={cx(cols, "text-2xs font-semibold text-fg-tertiary")} aria-hidden>
        <span>{t("catalog.add.specGroupCol")}</span>
        <span>{t("catalog.add.specNameCol")}</span>
        <span>{t("catalog.add.specValueCol")}</span>
      </div>
      {shown.map((s, i) => (
        <div key={i} className={cols}>
          <input
            value={s.group}
            onChange={(e) => upd(i, { group: e.target.value })}
            placeholder={t("catalog.add.specGroupShortPh")}
            aria-label={t("catalog.add.specGroupCol")}
            maxLength={60}
            size={1}
            className={cx(frame, "px-2 text-fg-secondary")}
          />
          <input
            value={s.name}
            onChange={(e) => upd(i, { name: e.target.value })}
            placeholder={t("catalog.add.specNameShortPh")}
            aria-label={t("catalog.add.specNameCol")}
            maxLength={80}
            size={1}
            className={cx(frame, "px-2 font-medium")}
          />
          <input
            value={s.value}
            onChange={(e) => upd(i, { value: e.target.value })}
            placeholder={t("catalog.add.specValueShortPh")}
            aria-label={t("catalog.add.specValueCol")}
            maxLength={300}
            size={1}
            className={cx(frame, "px-2")}
          />
          <button
            type="button"
            onClick={() => onChange(shown.filter((_, j) => j !== i))}
            title={t("catalog.add.remove")}
            className="flex min-h-control items-center justify-center rounded-xs text-fg-tertiary hover:bg-hover hover:text-danger-fg"
          >
            <Icon name="trash" />
            <span className="ady-sr-only">{t("catalog.add.remove")}</span>
          </button>
        </div>
      ))}
    </div>
  );
}

function VariantPhoto({
  media,
  image,
  title,
  onClick,
}: {
  media: MediaRef[];
  image: string | null;
  title: string;
  onClick: () => void;
}) {
  const m = image ? media.find((x) => x.asset_id === image) : undefined;
  const none = media.length === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={none}
      title={none ? t("catalog.add.variantPhotoNeed") : t("catalog.add.variantPhoto", { title })}
      className="flex size-7 flex-none items-center justify-center overflow-hidden rounded-xs border border-dashed border-control bg-sunken text-fg-tertiary hover:border-link hover:text-link disabled:cursor-not-allowed disabled:opacity-50"
    >
      {m?.preview_path ? (
        <img src={m.preview_path} alt="" className="size-full object-cover" />
      ) : (
        <Icon name="image" />
      )}
      <span className="ady-sr-only">{t("catalog.add.variantPhoto", { title })}</span>
    </button>
  );
}

function SeoBox({
  name,
  description,
  title,
  onTitle,
  meta,
  onMeta,
  media,
  image,
  onImage,
}: {
  name: string;
  description: string;
  title: string;
  onTitle: (v: string) => void;
  meta: string;
  onMeta: (v: string) => void;
  media: MediaRef[];
  image: string | null;
  onImage: (id: string | null) => void;
}) {
  const { membership } = useShell();
  const shownTitle = title.trim() || name.trim() || t("catalog.add.title");
  const shownMeta = (meta.trim() || description.trim()).slice(0, 160);
  const shareImage = (image ? media.find((m) => m.asset_id === image) : undefined) ?? media[0];
  const counter = (n: number, max: number) => (
    <span className={cx("ady-ledger ms-auto font-medium", n > max * 0.9 ? "text-warning-fg" : "")}>
      {t("catalog.add.seoCount", { count: n, max })}
    </span>
  );
  return (
    <div className="rounded-sm border border-card bg-surface p-2.5 grid gap-x-3 gap-y-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1 text-2xs font-semibold text-fg-tertiary">
            <span className="text-link">
              <Icon name="newspaper" />
            </span>
            {t("catalog.editor.seoTitle")}
            {counter(title.length, 70)}
          </span>
          <input
            value={title}
            onChange={(e) => onTitle(e.target.value)}
            placeholder={t("catalog.add.seoTitleShortPh")}
            maxLength={70}
            className={cx(frame, "px-2")}
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1 text-2xs font-semibold text-fg-tertiary">
            <span className="text-link">
              <Icon name="file" />
            </span>
            {t("catalog.editor.seoDescription")}
            {counter(meta.length, 320)}
          </span>
          <textarea
            value={meta}
            onChange={(e) => onMeta(e.target.value)}
            placeholder={t("catalog.add.seoDescriptionShortPh")}
            maxLength={320}
            rows={2}
            className="w-full rounded-xs border border-control bg-surface px-2 py-1.5 text-sm outline-none placeholder:text-fg-tertiary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          />
        </label>
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1 text-2xs font-semibold text-fg-tertiary">
            <span className="text-link">
              <Icon name="search" />
            </span>
            {t("catalog.add.seoPreview")}
          </span>
          <div className="flex gap-2.5 rounded-xs border border-card bg-sunken px-2.5 py-2">
            {shareImage?.preview_path ? (
              <img
                src={shareImage.preview_path}
                alt=""
                className="size-12 flex-none rounded-xs object-cover"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="truncate text-2xs text-fg-secondary">
                {searchCrumb(membership.org.name, "products", slugOf(name))}
              </p>
              <p className="truncate text-sm font-medium text-link" data-content="user">
                {shownTitle}
              </p>
              <p className="line-clamp-2 text-xs text-fg-secondary" data-content="user">
                {shownMeta || "—"}
              </p>
            </div>
          </div>
        </div>
        {media.length ? (
          <div className="flex flex-col gap-0.5" title={t("catalog.add.seoImageHelp")}>
            <span className="flex items-center gap-1 text-2xs font-semibold text-fg-tertiary">
              <span className="text-link">
                <Icon name="share" />
              </span>
              {t("catalog.add.seoImage")}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              <Chip on={image === null} onClick={() => onImage(null)}>
                <Icon name="image" />
                {t("catalog.add.seoImageCover")}
              </Chip>
              {media.map((m) => (
                <button
                  key={m.asset_id}
                  type="button"
                  onClick={() => onImage(m.asset_id)}
                  aria-pressed={image === m.asset_id}
                  aria-label={m.alt || t("catalog.add.seoImage")}
                  className={cx(
                    "size-7 overflow-hidden rounded-full border bg-sunken",
                    image === m.asset_id ? "ady-outline-brand border-[1.5px]" : "border-card",
                  )}
                >
                  {m.preview_path ? (
                    <img src={m.preview_path} alt="" className="size-full object-cover" />
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
